import { ObjectShape } from 'yup/lib/object';
import { ConfigurationType } from './configuration-type';
import * as yup from 'yup';
import { AccordionGeneralityForm } from 'civil-status/view-models/accordion-generality-form';
import {
  fiscalCodeRegex,
  portionRegex,
  portionRegexWithoutGlobal,
  regexOnlyNumber,
  regrexOnlyNumberWithoutGlobal,
  regVolRegex,
} from 'shell/constants/regex';
import { DateTime } from 'luxon';
import { ActFormVM, LastStepVM, SectionVM } from 'civil-status/view-models/step-form';
import { ID_RESIDENCE_LIST } from 'civil-status/components/step/form/last-step/accordions/subject/form/list/list-residence-born';
import { ID_ITALIAN_NATION } from 'shell/constants/id-italian-nation';
import {
  ID_OTHER_AUTHORITY,
  ItemValoreDominioAttoVM,
} from 'civil-status/components/step/form/last-step/accordions/section/list/list-authority';
import { typePresence } from './type-presence';
import { isObjectEmpty } from 'shell/utils/is-object-empty';
import { clearSectionMomentary } from 'civil-status/utils/clear-section';
import { clearSubjectMomentary } from 'civil-status/utils/clear-subject';
import { IDChangeSurname } from './id-change-surname';
import { ID_OTHER_REASON } from './id-other-reason';
import { ID_OTHER_PLACE } from './id-other-place';
import { ID_OTHER_RESIDENCE } from './id-other-residence';
import { IdPropertyRegime } from './id-property-regime';
import { ItemVM } from 'shell/view-models/item';
import { ID_COMMUNICATION } from 'civil-status/components/step/form/last-step/accordions/section/list/list-communication';
import { IDTypeRequest } from './id-type-request';
import { ID_OTHER_RITUAL_RELIGIOUS } from './id-ritual-religious';
import { CodeTypeAct } from './code-type-act';
import { IdResidence } from './id-residence';
import { MUNICIPALITY_MILANO } from './municipality-milano';
import { arrSubjectControllGenderF, arrSubjectControllGenderM } from './subject-gender';
import { ID_GENDERS } from 'shell/components/shared/gender';
import { CivilStatusID } from 'shell/constants/civil-status';
import { ID_CHOOSING_SURNAME } from 'civil-status/components/step/form/last-step/accordions/subject/form/list/list-reason-choosing-surname';
import { ID_REGISTER } from 'civil-status/components/shared/list-register';
import { ID_ALTRO_COMUNE } from 'shell/components/shared/municipality-list';
import { CodeFormatBirthDate } from './code-format-birth-date';

// Lista delle serie prese per tipo evento
const deathSeries = ['A', 'B', 'C', 'C1', 'C2'];

const marriageSeries = ['A', 'B', 'C', 'C1', 'C2', 'C3'];

const birthSeries = ['A', 'B'];

/**
 * Funzione per controllare se la serie inserita per l'atto cartaceo
 *  nel form dell'ultimo step
 *  sia corretta a seconda del tipo di evento
 * @param {LastStepVM} values valori dell'ultimo step
 * @param {object} context contesto della validazione
 * @returns {true | yup.ValidationError} true se la serie  corretta, altrimenti ValidationError
 */
const serieFunction = (values: LastStepVM, context: { path?: string }) => {
  // MATRIMONI :  A - B - C - C1- C2 -C3
  // NASCITA : A - B
  // UNIONI CIVILI no serie
  // CITTADINANZA : no serie
  // MORTI  :  A - B - C- C1 C2
  const castedActForm = values.actForm as ActFormVM;

  if (
    values.eventType === ID_REGISTER.MORTE &&
    castedActForm.serie &&
    !deathSeries.includes(castedActForm.serie)
  ) {
    return new yup.ValidationError(
      `La serie deve essere ${deathSeries.join(' o ')}`,
      undefined,
      `${context.path}.actForm.serie`
    );
  }

  if (
    values.eventType === ID_REGISTER.MATRIMONIO &&
    castedActForm.serie &&
    !marriageSeries.includes(castedActForm.serie)
  ) {
    return new yup.ValidationError(
      `La serie deve essere ${marriageSeries.join(' o ')}`,
      undefined,
      `${context.path}.actForm.serie`
    );
  }

  if (
    values.eventType === ID_REGISTER.NASCITA &&
    castedActForm.serie &&
    !birthSeries.includes(castedActForm.serie)
  ) {
    return new yup.ValidationError(
      `La serie deve essere ${birthSeries.join(' o ')}`,
      undefined,
      `${context.path}.actForm.serie`
    );
  }
  return true;
};

const schemaBASEANNI = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          isMinorDeclarant: yup.boolean().nullable(),
          formatBirthDate: yup.object().nullable(),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('formatBirthDate', {
              is: (formatBirthDate: ItemVM) =>
                formatBirthDate?.id?.toString() === CodeFormatBirthDate.SoloAnni,
              then: yup.date().typeError('Data non valida').nullable(),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASE = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          isMinorDeclarant: yup.boolean().nullable(),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isMinorDeclarant', {
              is: (isMinorDeclarant: boolean) => isMinorDeclarant,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  dateFormationAct.plus({ day: 1 }),
                  "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASEANMD = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          isMinorDeclarant: yup.boolean().nullable(),
          formatBirthDate: yup.object().nullable(),
          age: yup
            .number()
            .transform((value, originalValue) => (originalValue === '' ? undefined : value))
            .typeError('Età non valida')
            .nullable()
            .when('formatBirthDate', {
              is: (formatBirthDate: ItemVM) =>
                formatBirthDate?.id?.toString() === CodeFormatBirthDate.SoloAnni,
              then: yup
                .number()
                .transform((value, originalValue) => (originalValue === '' ? undefined : value))
                .typeError('Età non valida')
                .nullable()
                .required('Campo obbligatorio'),
              otherwise: yup
                .number()
                .transform((value, originalValue) => (originalValue === '' ? undefined : value))
                .typeError('Età non valida')
                .nullable(),
            }),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('formatBirthDate', {
              is: (formatBirthDate: ItemVM) =>
                formatBirthDate?.id?.toString() === CodeFormatBirthDate.SoloAnni,
              then: yup.date().typeError('Data non valida').nullable(),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .when('isMinorDeclarant', {
                  is: (isMinorDeclarant: boolean) => isMinorDeclarant,
                  then: yup
                    .date()
                    .typeError('Data non valida')
                    .nullable()
                    .max(
                      dateFormationAct.plus({ day: 1 }),
                      "La data deve essere minore uguale della data di formazione dell'atto"
                    )
                    .required('Campo obbligatorio'),
                  otherwise: yup
                    .date()
                    .typeError('Data non valida')
                    .nullable()
                    .max(
                      isDichiarante
                        ? dateFormationAct.minus({ years: 18 })
                        : dateFormationAct.plus({ day: 1 }),
                      isDichiarante
                        ? 'Il soggetto deve essere maggiorenne'
                        : "La data deve essere minore uguale della data di formazione dell'atto"
                    )
                    .required('Campo obbligatorio'),
                }),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante Ã¨ true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante Ã¨ true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASEMAG = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(dateFormationAct.minus({ years: 18 }), 'Il soggetto deve essere maggiorenne')
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASEREMI = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          isMinorDeclarant: yup.boolean().nullable(),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isMinorDeclarant', {
              is: (isMinorDeclarant: boolean) => isMinorDeclarant,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  dateFormationAct.plus({ day: 1 }),
                  "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          if (
            valuesCasted.italianResidence?.municipality &&
            valuesCasted.italianResidence?.municipality.code !== MUNICIPALITY_MILANO.code
          ) {
            return new yup.ValidationError(
              'Il cittadino deve risiedere a Milano',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        }),
      // .test('foreignResidence', 'Campo obbligatorio', (values) => {
      //   // controllo residenza foreign
      //   const valuesCasted = values as unknown as AccordionGeneralityForm;

      //   if (
      //     valuesCasted.typeResidence === IdResidence.Foreign &&
      //     !valuesCasted.foreignResidence?.state?.id
      //   ) {
      //     return new yup.ValidationError(
      //       'Campo obbligatorio',
      //       undefined,
      //       `${keySubject}.generality.foreignResidence.state`
      //     );
      //   }

      //   return true;
      // }),
    });
};

const schemaRICHUCOPF = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          isMinorDeclarant: yup.boolean().nullable(),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isMinorDeclarant', {
              is: (isMinorDeclarant: boolean) => isMinorDeclarant,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  dateFormationAct.plus({ day: 1 }),
                  "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        // .test('conditionsItem', 'Campo obbligatorio', (values) => {
        //   // controllo Condizioni dichiarante se isDichiarante è true
        //   const valuesCasted = values as unknown as AccordionGeneralityForm;

        //   if (!valuesCasted.conditionsItem?.id && isDichiarante) {
        //     return new yup.ValidationError(
        //       'Campo obbligatorio',
        //       undefined,
        //       `${keySubject}.generality.conditionsItem`
        //     );
        //   }

        //   return true;
        // })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && valuesCasted.isSignatory) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        }),
    });
};

const schemaBASE14 = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 14 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve avere più o uguale di 14 anni'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASEMIN = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .min(dateFormationAct.minus({ years: 18 }), 'Il soggetto deve essere minorenne')
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaBaseF = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable(),
          surname: yup.string().nullable(),
          gender: yup.string().nullable(),
          nationality: yup.object().nullable().shape({}),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          isMinorDeclarant: yup.boolean().nullable(),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isMinorDeclarant', {
              is: (isMinorDeclarant: boolean) => isMinorDeclarant,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  dateFormationAct.plus({ day: 1 }),
                  "La data deve essere minore uguale della data di formazione dell'atto"
                ),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                ),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('', 'Campi obbligatorio', (values) => {
          // controlli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          const valuesMomentary = {
            ...clearSubjectMomentary({ generality: { ...valuesCasted } }),
          };

          const hasFields = !isObjectEmpty((valuesMomentary || {}) as Record<string, unknown>);

          const errors = [];

          if (hasFields && !valuesCasted?.name) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.name`
              )
            );
          }

          if (hasFields && !valuesCasted?.surname) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.surname`
              )
            );
          }

          if (hasFields && !valuesCasted?.gender) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.gender`
              )
            );
          }

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            errors.push(
              new yup.ValidationError(
                'Il soggetto deve essere una femmina',
                undefined,
                `${keySubject}.generality.gender`
              )
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            errors.push(
              new yup.ValidationError(
                'Il soggetto deve essere una maschio',
                undefined,
                `${keySubject}.generality.gender`
              )
            );
          }

          if (hasFields && !valuesCasted?.nationality?.value) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.nationality`
              )
            );
          }

          if (
            hasFields &&
            (!valuesCasted?.birthDate || valuesCasted?.birthDate?.toString() === 'Invalid Date')
          ) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.birthDate`
              )
            );
          }

          if (hasFields && !valuesCasted.conditionsItem?.id && isDichiarante) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.conditionsItem`
              )
            );
          }

          if (
            hasFields &&
            !valuesCasted.isForeignBorn &&
            !valuesCasted.italian?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          if (hasFields && valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          if (
            hasFields &&
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }
          if (
            hasFields &&
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          if (errors.length === 0) {
            return true;
          }

          return new yup.ValidationError(errors);
        }),
    });
};

const schemaSOGMINF = (keySubject: string) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable(),
        })
        .test('', 'Campi obbligatorio', (values) => {
          // controlli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          const valuesMomentary = {
            ...clearSubjectMomentary({ generality: { ...valuesCasted } }),
          };

          const hasFields = !isObjectEmpty((valuesMomentary || {}) as Record<string, unknown>);

          const errors = [];

          if (hasFields && !valuesCasted?.name) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.name`
              )
            );
          }

          if (errors.length === 0) {
            return true;
          }

          return new yup.ValidationError(errors);
        }),
    });
};

const schemaOFFICIVF = (keySubject: string) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          ufcDelegate: yup.string().nullable(),
          name: yup.string().nullable(),
          surname: yup.string().nullable(),
        })
        .test('', 'Campi obbligatorio', (values) => {
          // controlli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          const valuesMomentary = {
            ...clearSubjectMomentary({ generality: { ...valuesCasted } }),
          };

          const hasFields = !isObjectEmpty((valuesMomentary || {}) as Record<string, unknown>);

          const errors = [];

          if (hasFields && !valuesCasted?.ufcDelegate) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.ufcDelegate`
              )
            );
          }

          if (hasFields && !valuesCasted?.name) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.name`
              )
            );
          }

          if (hasFields && !valuesCasted?.surname) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.surname`
              )
            );
          }

          if (errors.length === 0) {
            return true;
          }

          return new yup.ValidationError(errors);
        }),
    });
};

const schemaOFFITRASAF = (keySubject: string) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable(),
          surname: yup.string().nullable(),
        })
        .test('', 'Campi obbligatorio', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          const valuesMomentary = {
            ...clearSubjectMomentary({ generality: { ...valuesCasted } }),
          };

          const hasFields = !isObjectEmpty((valuesMomentary || {}) as Record<string, unknown>);

          const errors = [];

          if (hasFields && !valuesCasted?.name) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.name`
              )
            );
          }

          if (hasFields && !valuesCasted?.surname) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.surname`
              )
            );
          }

          if (errors.length === 0) {
            return true;
          }

          return new yup.ValidationError(errors);
        }),
    });
};

const schemaPERICCIV = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          isMinorDeclarant: yup.boolean().nullable(),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isMinorDeclarant', {
              is: (isMinorDeclarant: boolean) => isMinorDeclarant,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  dateFormationAct.plus({ day: 1 }),
                  "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
            }),
          isImminentDangerOfLife: yup.boolean().nullable(),
          verificationOfWill: yup
            .string()
            .nullable()
            .when('isImminentDangerOfLife', {
              is: (isImminentDangerOfLife: boolean) => isImminentDangerOfLife,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
          civilStatusItem: yup.object().nullable(),
        })
        .test('civilStatusItem', 'Campo Stato civile non congruente', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;
          if (
            Boolean(valuesCasted.civilStatusItem) &&
            (valuesCasted.civilStatusItem?.id?.toString() === CivilStatusID.coniugato ||
              valuesCasted.civilStatusItem?.id?.toString() === CivilStatusID.unitoCivilmente)
          ) {
            return new yup.ValidationError(
              'Il soggetto non deve essere coniugato/unito civilmente',
              undefined,
              `${keySubject}.generality.civilStatusItem`
            );
          }

          return true;
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaULTLUOGOF = () => {
  return yup
    .object()
    .nullable()
    .shape({
      dateLastSighting: yup.date().typeError('Data non valida').nullable(),
      isForeign: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    });
};

const schemaBASEDIR = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          isMinorDeclarant: yup.boolean().nullable(),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isMinorDeclarant', {
              is: (isMinorDeclarant: boolean) => isMinorDeclarant,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  dateFormationAct.plus({ day: 1 }),
                  "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
            }),
          dateStartResidence: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASE02OPFI = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          isRequiredFromOtherAccordion: yup.boolean().nullable(),
          name: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          surname: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          gender: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          nationality: yup.object().nullable(),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                ),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.isWithTwins &&
            !Boolean(valuesCasted.allTwins) &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItem?.id &&
            isDichiarante &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItemSignatory?.id &&
            isFirmatario &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.isForeignBorn &&
            !valuesCasted.italian?.municipality?.description &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.isForeignBorn &&
            !valuesCasted.foreign?.state?.id &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASE02C = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          isRequiredFromOtherAccordion: yup.boolean().nullable(),
          name: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          surname: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          gender: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          nationality: yup.object().nullable(),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                ),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.isWithTwins &&
            !Boolean(valuesCasted.allTwins) &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItem?.id &&
            isDichiarante &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItemSignatory?.id &&
            isFirmatario &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.isForeignBorn &&
            !valuesCasted.italian?.municipality?.description &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.isForeignBorn &&
            !valuesCasted.foreign?.state?.id &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASE02A = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable(),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          formatBirthDate: yup.object().nullable(),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('formatBirthDate', {
              is: (formatBirthDate: ItemVM) =>
                formatBirthDate?.id?.toString() === CodeFormatBirthDate.SoloAnni,
              then: yup.date().typeError('Data non valida').nullable(),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASE02 = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable(),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        }),
    });
};

const schemaRICC = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isRequiredFromOtherAccordion: yup.boolean().nullable(),
          name: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          surname: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          gender: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                ),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItem?.id &&
            isDichiarante &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItemSignatory?.id &&
            isFirmatario &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.isForeignBorn &&
            !valuesCasted.italian?.municipality?.description &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.isForeignBorn &&
            !valuesCasted.foreign?.state?.id &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        }),
    });
};

const schemaRIC1FO = (keySubject: string, isDichiarante: boolean, dateFormationAct: DateTime) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isRequiredFromOtherAccordion: yup.boolean().nullable(),
          name: yup.string().nullable(),
          surname: yup.string().nullable(),
          gender: yup.string().nullable(),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                ),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                ),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('', 'Campi obbligatorio', (values) => {
          // controlli
          // Nota: solo il nome è sottoposto al doppio controllo

          const valuesCasted = values as unknown as AccordionGeneralityForm;

          // const valuesMomentary = {
          //   ...clearSubjectMomentary({ generality: { ...valuesCasted } }),
          // };

          // const hasFields = !isObjectEmpty((valuesMomentary || {}) as Record<string, unknown>);

          const errors = [];

          if (!valuesCasted.name && valuesCasted.isRequiredFromOtherAccordion) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.name`
              )
            );
          }

          if (!valuesCasted.surname && valuesCasted.isRequiredFromOtherAccordion) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.surname`
              )
            );
          }

          if (!valuesCasted.gender && valuesCasted.isRequiredFromOtherAccordion) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.gender`
              )
            );
          }

          if (
            (!valuesCasted.birthDate || valuesCasted.birthDate?.toString() === 'Invalid Date') &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.birthDate`
              )
            );
          }

          if (
            !valuesCasted.conditionsItem?.id &&
            isDichiarante &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.conditionsItem`
              )
            );
          }

          if (
            !valuesCasted.isForeignBorn &&
            !valuesCasted.italian?.municipality?.description &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.italian.municipality`
              )
            );
          }

          if (
            valuesCasted.isForeignBorn &&
            !valuesCasted.foreign?.state?.id &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.foreign.state`
              )
            );
          }

          if (errors.length === 0) {
            return true;
          }

          return new yup.ValidationError(errors);
        }),
    });
};

const schemaRICFO = (keySubject: string, isDichiarante: boolean, dateFormationAct: DateTime) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isRequiredFromOtherAccordion: yup.boolean().nullable(),
          name: yup.string().nullable(),
          surname: yup.string().nullable(),
          gender: yup.string().nullable(),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                ),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                ),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('', 'Campi obbligatorio', (values) => {
          // controlli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          const valuesMomentary = {
            ...clearSubjectMomentary({ generality: { ...valuesCasted } }),
          };

          const hasFields = !isObjectEmpty((valuesMomentary || {}) as Record<string, unknown>);

          const errors = [];

          if (!valuesCasted.name && (hasFields || valuesCasted.isRequiredFromOtherAccordion)) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.name`
              )
            );
          }

          if (!valuesCasted.surname && (hasFields || valuesCasted.isRequiredFromOtherAccordion)) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.surname`
              )
            );
          }

          if (!valuesCasted.gender && (hasFields || valuesCasted.isRequiredFromOtherAccordion)) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.gender`
              )
            );
          }

          if (
            (!valuesCasted.birthDate || valuesCasted.birthDate?.toString() === 'Invalid Date') &&
            (hasFields || valuesCasted.isRequiredFromOtherAccordion)
          ) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.birthDate`
              )
            );
          }

          if (
            !valuesCasted.conditionsItem?.id &&
            isDichiarante &&
            (hasFields || valuesCasted.isRequiredFromOtherAccordion)
          ) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.conditionsItem`
              )
            );
          }

          if (
            !valuesCasted.isForeignBorn &&
            !valuesCasted.italian?.municipality?.description &&
            (hasFields || valuesCasted.isRequiredFromOtherAccordion)
          ) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.italian.municipality`
              )
            );
          }

          if (
            valuesCasted.isForeignBorn &&
            !valuesCasted.foreign?.state?.id &&
            (hasFields || valuesCasted.isRequiredFromOtherAccordion)
          ) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.generality.foreign.state`
              )
            );
          }

          if (errors.length === 0) {
            return true;
          }

          return new yup.ValidationError(errors);
        }),
    });
};

const schemaRIC = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASEC = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isRequiredFromOtherAccordion: yup.boolean().nullable(),
          name: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          surname: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          gender: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          nationality: yup
            .object()
            .nullable()
            .shape({})
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.object().nullable().shape({}).required('Campo obbligatorio'),
              otherwise: yup.object().nullable().shape({}),
            }),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                ),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItem?.id &&
            isDichiarante &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItemSignatory?.id &&
            isFirmatario &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.isForeignBorn &&
            !valuesCasted.italian?.municipality?.description &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.isForeignBorn &&
            !valuesCasted.foreign?.state?.id &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaFIGLIOTRNAC = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isRequiredFromOtherAccordion: yup.boolean().nullable(),
          name: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          surname: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          newSurname: yup.string().nullable(),
          gender: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          nationality: yup
            .object()
            .nullable()
            .shape({})
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.object().nullable().shape({}).required('Campo obbligatorio'),
              otherwise: yup.object().nullable().shape({}),
            }),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                ),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          italianAct: yup
            .object()
            .nullable()
            .shape({})
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup
                .object()
                .nullable()
                .shape({
                  municipality: yup
                    .object()
                    .nullable()
                    .shape({})
                    .nullable()
                    .required('Campo obbligatorio'),
                }),
              otherwise: yup.object().nullable().shape({}),
            }),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItem?.id &&
            isDichiarante &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItemSignatory?.id &&
            isFirmatario &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.isForeignBorn &&
            !valuesCasted.italian?.municipality?.description &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.isForeignBorn &&
            !valuesCasted.foreign?.state?.id &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id &&
            values.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        })
        .test('idActDigital', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeAct === CodeTypeAct.attoDigitale &&
            values.isRequiredFromOtherAccordion &&
            !valuesCasted.idActDigital
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.idActDigital`
            );
          }

          return true;
        })
        .test('idActPaper', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeAct === CodeTypeAct.attoCartaceo &&
            values.isRequiredFromOtherAccordion &&
            !valuesCasted.idActPaper
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.idActPaper`
            );
          }

          return true;
        }),
    });
};

const schemaNEO = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          isMinorDeclarant: yup.boolean().nullable(),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isMinorDeclarant', {
              is: (isMinorDeclarant: boolean) => isMinorDeclarant,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  dateFormationAct.plus({ day: 1 }),
                  "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
            }),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
          civilStatusItem: yup.object().nullable(),
        })
        .test('civilStatusItem', 'Campo Stato civile non congruente', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;
          if (
            Boolean(valuesCasted.civilStatusItem) &&
            (valuesCasted.civilStatusItem?.id?.toString() === CivilStatusID.coniugato ||
              valuesCasted.civilStatusItem?.id?.toString() === CivilStatusID.unitoCivilmente)
          ) {
            return new yup.ValidationError(
              'Il soggetto non deve essere coniugato/unito civilmente',
              undefined,
              `${keySubject}.generality.civilStatusItem`
            );
          }

          return true;
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASEPERD = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          reasonLossCitizenship: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        // .test('foreignResidence', 'Campo obbligatorio', (values) => {
        //   // controllo residenza foreign
        //   const valuesCasted = values as unknown as AccordionGeneralityForm;

        //   if (
        //     valuesCasted.typeResidence === IdResidence.Foreign &&
        //     !valuesCasted.foreignResidence?.municipality?.description
        //   ) {
        //     return new yup.ValidationError(
        //       'Campo obbligatorio',
        //       undefined,
        //       `${keySubject}.generality.foreignResidence.municipality`
        //     );
        //   }

        //   return true;
        // })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASEAVV = () => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup.object().shape({
        name: yup.string().nullable().required('Campo obbligatorio'),
        surname: yup.string().nullable().required('Campo obbligatorio'),
        lawyerForum: yup.string().nullable().required('Campo obbligatorio'),
      }),
    });
};

const schemaBASEAVVC = () => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup.object().shape({
        isRequiredFromOtherAccordion: yup.boolean().nullable(),
        name: yup
          .string()
          .nullable()
          .when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.string().nullable().required('Campo obbligatorio'),
            otherwise: yup.string().nullable(),
          }),
        surname: yup
          .string()
          .nullable()
          .when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.string().nullable().required('Campo obbligatorio'),
            otherwise: yup.string().nullable(),
          }),
        lawyerForum: yup
          .string()
          .nullable()
          .when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.string().nullable().required('Campo obbligatorio'),
            otherwise: yup.string().nullable(),
          }),
      }),
    });
};

const schemaBASEAVV2C = (keySubject?: string) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup.object().shape({
        isRequiredFromOtherAccordion: yup.boolean().nullable(),
        name: yup
          .string()
          .nullable()
          .when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.string().nullable().required('Campo obbligatorio'),
            otherwise: yup.string().nullable(),
          }),
        surname: yup
          .string()
          .nullable()
          .when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.string().nullable().required('Campo obbligatorio'),
            otherwise: yup.string().nullable(),
          }),
        lawyerForum: yup
          .string()
          .nullable()
          .when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.string().nullable().required('Campo obbligatorio'),
            otherwise: yup.string().nullable(),
          }),
        italianResidence: yup.object().nullable().shape({}),
        foreignResidence: yup.object().nullable().shape({}),
        typeResidence: yup.string().nullable(),
      }),
    })
    .test('italianResidence', 'Campo obbligatorio', (values) => {
      // controllo residenza italian
      const valuesCasted = values as unknown as AccordionGeneralityForm;

      if (
        valuesCasted.typeResidence === IdResidence.Italian &&
        !valuesCasted.italianResidence?.municipality?.description &&
        valuesCasted.isRequiredFromOtherAccordion
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.generality.italianResidence.municipality`
        );
      }

      return true;
    })
    .test('foreignResidence', 'Campo obbligatorio', (values) => {
      // controllo residenza foreign
      const valuesCasted = values as unknown as AccordionGeneralityForm;

      if (
        valuesCasted.typeResidence === IdResidence.Foreign &&
        !valuesCasted.foreignResidence?.state?.id &&
        valuesCasted.isRequiredFromOtherAccordion
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.generality.foreignResidence.state`
        );
      }

      return true;
    });
};

const schemaBASEAVV3 = () => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup.object().shape({
        name: yup.string().nullable().required('Campo obbligatorio'),
        surname: yup.string().nullable().required('Campo obbligatorio'),
        lawyerForum: yup.string().nullable(),
        dateCommunication: yup
          .date()
          .typeError('Data non valida')
          .nullable()
          .when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
            otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
          }),
        protocol: yup
          .string()
          .nullable()
          .when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.string().nullable().required('Campo obbligatorio'),
            otherwise: yup.string().nullable(),
          }),
      }),
    });
};

const schemaBASEUC = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          otherCitizenship: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        // .test('foreignResidence', 'Campo obbligatorio', (values) => {
        //   // controllo residenza foreign
        //   const valuesCasted = values as unknown as AccordionGeneralityForm;

        //   if (
        //     valuesCasted.typeResidence === IdResidence.Foreign &&
        //     !valuesCasted.foreignResidence?.municipality?.description
        //   ) {
        //     return new yup.ValidationError(
        //       'Campo obbligatorio',
        //       undefined,
        //       `${keySubject}.generality.foreignResidence.municipality`
        //     );
        //   }

        //   return true;
        // })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASEG = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          relationship: yup.string().nullable().required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        // .test('foreignResidence', 'Campo obbligatorio', (values) => {
        //   // controllo residenza foreign
        //   const valuesCasted = values as unknown as AccordionGeneralityForm;

        //   if (
        //     valuesCasted.typeResidence === IdResidence.Foreign &&
        //     !valuesCasted.foreignResidence?.municipality?.description
        //   ) {
        //     return new yup.ValidationError(
        //       'Campo obbligatorio',
        //       undefined,
        //       `${keySubject}.generality.foreignResidence.municipality`
        //     );
        //   }

        //   return true;
        // })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaINTCIT = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
          // data document
          documentNumber: yup.string().nullable().required('Campo obbligatorio'),
          documentTypology: yup.string().nullable().required('Campo obbligatorio'),
          documentExpiryDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .required('Campo obbligatorio'),
          documentReleaseDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .required('Campo obbligatorio'),
          documentReleaseFrom: yup.string().nullable().required('Campo obbligatorio'),
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        // .test('foreignResidence', 'Campo obbligatorio', (values) => {
        //   // controllo residenza foreign
        //   const valuesCasted = values as unknown as AccordionGeneralityForm;

        //   if (
        //     valuesCasted.typeResidence === IdResidence.Foreign &&
        //     !valuesCasted.foreignResidence?.municipality?.description
        //   ) {
        //     return new yup.ValidationError(
        //       'Campo obbligatorio',
        //       undefined,
        //       `${keySubject}.generality.foreignResidence.municipality`
        //     );
        //   }

        //   return true;
        // })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaINTCIT3 = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          otherCitizenship: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
          // data document
          documentNumber: yup.string().nullable().required('Campo obbligatorio'),
          documentTypology: yup.string().nullable().required('Campo obbligatorio'),
          documentExpiryDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .required('Campo obbligatorio'),
          documentReleaseDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .required('Campo obbligatorio'),
          documentReleaseFrom: yup.string().nullable().required('Campo obbligatorio'),
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        // .test('foreignResidence', 'Campo obbligatorio', (values) => {
        //   // controllo residenza foreign
        //   const valuesCasted = values as unknown as AccordionGeneralityForm;

        //   if (
        //     valuesCasted.typeResidence === IdResidence.Foreign &&
        //     !valuesCasted.foreignResidence?.municipality?.description
        //   ) {
        //     return new yup.ValidationError(
        //       'Campo obbligatorio',
        //       undefined,
        //       `${keySubject}.generality.foreignResidence.municipality`
        //     );
        //   }

        //   return true;
        // })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASEDEC = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          civilStatusItem: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          if (
            !valuesCasted.isForeignBorn &&
            valuesCasted.italian?.municipality?.code?.toString() === ID_ALTRO_COMUNE &&
            !valuesCasted.italian?.otherMunicipality
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.otherMunicipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('italianResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Italian &&
            !valuesCasted.italianResidence?.municipality?.description
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italianResidence.municipality`
            );
          }

          return true;
        })
        .test('foreignResidence', 'Campo obbligatorio', (values) => {
          // controllo residenza foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.typeResidence === IdResidence.Foreign &&
            !valuesCasted.foreignResidence?.state?.id
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreignResidence.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASE02DEC = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .shape({
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          nationality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          civilStatusItem: yup.object().nullable().shape({}).required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().nullable().shape({}),
          italianResidence: yup.object().nullable().shape({}),
          foreignResidence: yup.object().nullable().shape({}),
          typeResidence: yup.string().nullable(),
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })

        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        }),
    });
};

const schemaBASECONI = () => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup.object().shape({
        name: yup.string().nullable().required('Campo obbligatorio'),
        surname: yup.string().nullable().required('Campo obbligatorio'),
      }),
    });
};

const schemaSOGMIN = () => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup.object().shape({
        name: yup.string().nullable().required('Campo obbligatorio'),
      }),
    });
};

const schemaBASECONIC = () => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup.object().shape({
        isRequiredFromOtherAccordion: yup.boolean().nullable(),
        name: yup
          .string()
          .nullable()
          .when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.string().nullable().required('Campo obbligatorio'),
            otherwise: yup.string().nullable(),
          }),
        surname: yup
          .string()
          .nullable()
          .when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.string().nullable().required('Campo obbligatorio'),
            otherwise: yup.string().nullable(),
          }),
      }),
    });
};

const schemaSOGMINC = () => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup.object().shape({
        isRequiredFromOtherAccordion: yup.boolean().nullable(),
        name: yup
          .string()
          .nullable()
          .when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.string().nullable().required('Campo obbligatorio'),
            otherwise: yup.string().nullable(),
          }),
      }),
    });
};

const schemaOFFICIV = () => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup.object().shape({
        ufcDelegate: yup.string().nullable().required('Campo obbligatorio'),
        name: yup.string().nullable().required('Campo obbligatorio'),
        surname: yup.string().nullable().required('Campo obbligatorio'),
      }),
    });
};

const schemaOFFIACATT = () => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup.object().shape({
        name: yup.string().nullable().required('Campo obbligatorio'),
        surname: yup.string().nullable().required('Campo obbligatorio'),
        ufcDelegate: yup
          .string()
          .nullable()
          .when(['name', 'surname'], {
            is: (name: string, surname: string) => Boolean(name) && Boolean(surname),
            then: yup.string().nullable(),
            otherwise: yup.string().nullable().required('Campo obbligatorio'),
          }),
      }),
    });
};

const schemaSEGRCIV = () => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup.object().shape({
        roloSecretary: yup.string().nullable().required('Campo obbligatorio'),
        name: yup.string().nullable().required('Campo obbligatorio'),
        surname: yup.string().nullable().required('Campo obbligatorio'),
      }),
    });
};

const schemaNASC = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime,
  withReasonDelay: boolean
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .nullable()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          reasonChoosingSurname: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          clarificationSurname: yup
            .string()
            .nullable()
            .when('reasonChoosingSurname', {
              is: (reasonChoosingSurname: ItemVM) =>
                reasonChoosingSurname?.id?.toString() === ID_CHOOSING_SURNAME.Altro,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          birthTime: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          birthPlaceItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          codeIstat: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
          descriptionBirthPlace: yup.string().required('Campo obbligatorio'),
          verificationTypeItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          italian: yup
            .object()
            .nullable()
            .shape({
              municipality: yup
                .object()
                .nullable()
                .shape({})
                .nullable()
                .required('Campo obbligatorio'),
            }),
          foreign: yup.object().shape({}),
          residenceId: yup.string().notRequired().nullable(),
          italianResidence: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .when('residenceId', {
              is: (residenceId: string) =>
                residenceId === ID_RESIDENCE_LIST.ConMadre ||
                residenceId === ID_RESIDENCE_LIST.ConPadre,
              then: yup.object().shape({}).nullable().required('Campo obbligatorio'),
              otherwise: yup.object().shape({}).nullable(),
            }),
          reasonDelay: yup.string().nullable(),
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('reasonDelay', 'Campo obbligatorio', (values) => {
          // controllo se presente motivo ritardo comunicazione
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.reasonDelay && withReasonDelay) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.reasonDelay`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        }),
    });
};

const schemaNASC04 = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime,
  withReasonDelay: boolean
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .nullable()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          reasonChoosingSurname: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          clarificationSurname: yup
            .string()
            .nullable()
            .when('reasonChoosingSurname', {
              is: (reasonChoosingSurname: ItemVM) =>
                reasonChoosingSurname?.id?.toString() === ID_CHOOSING_SURNAME.Altro,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          birthTime: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          birthPlaceItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          codeIstat: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
          descriptionBirthPlace: yup.string().required('Campo obbligatorio'),
          verificationTypeItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          italian: yup
            .object()
            .nullable()
            .shape({
              municipality: yup
                .object()
                .nullable()
                .shape({})
                .nullable()
                .required('Campo obbligatorio'),
            }),
          foreign: yup.object().shape({}),
          reasonDelay: yup.string().nullable(),
          necropsyDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .required('Campo obbligatorio'),
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('reasonDelay', 'Campo obbligatorio', (values) => {
          // controllo se presente motivo ritardo comunicazione
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.reasonDelay && withReasonDelay) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.reasonDelay`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        }),
    });
};

const schemaNASC05 = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime,
  withReasonDelay: boolean
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .nullable()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          reasonChoosingSurname: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          clarificationSurname: yup
            .string()
            .nullable()
            .when('reasonChoosingSurname', {
              is: (reasonChoosingSurname: ItemVM) =>
                reasonChoosingSurname?.id?.toString() === ID_CHOOSING_SURNAME.Altro,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          birthTime: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          birthPlaceItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          codeIstat: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
          descriptionBirthPlace: yup.string().required('Campo obbligatorio'),
          verificationTypeItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          italian: yup
            .object()
            .nullable()
            .shape({
              municipality: yup
                .object()
                .nullable()
                .shape({})
                .nullable()
                .required('Campo obbligatorio'),
            }),
          foreign: yup.object().shape({}),
          reasonDelay: yup.string().nullable(),
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('reasonDelay', 'Campo obbligatorio', (values) => {
          // controllo se presente motivo ritardo comunicazione
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.reasonDelay && withReasonDelay) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.reasonDelay`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        }),
    });
};

const schemaNASC06 = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime,
  withReasonDelay: boolean
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .nullable()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          reasonChoosingSurname: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          clarificationSurname: yup
            .string()
            .nullable()
            .when('reasonChoosingSurname', {
              is: (reasonChoosingSurname: ItemVM) =>
                reasonChoosingSurname?.id?.toString() === ID_CHOOSING_SURNAME.Altro,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          birthTime: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          birthPlaceItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          codeIstat: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
          descriptionBirthPlace: yup.string().required('Campo obbligatorio'),
          verificationTypeItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          italian: yup
            .object()
            .nullable()
            .shape({
              municipality: yup
                .object()
                .nullable()
                .shape({})
                .nullable()
                .required('Campo obbligatorio'),
            }),
          foreign: yup.object().shape({}),
          reasonDelay: yup.string().nullable(),
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('reasonDelay', 'Campo obbligatorio', (values) => {
          // controllo se presente motivo ritardo comunicazione
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.reasonDelay && withReasonDelay) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.reasonDelay`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        }),
    });
};

const schemaNASC99 = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime,
  withReasonDelay: boolean
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .nullable()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          bornTypology: yup.string().nullable().required('Campo obbligatorio'),
          reasonChoosingSurname: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          clarificationSurname: yup
            .string()
            .nullable()
            .when('reasonChoosingSurname', {
              is: (reasonChoosingSurname: ItemVM) =>
                reasonChoosingSurname?.id?.toString() === ID_CHOOSING_SURNAME.Altro,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          birthTime: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .required('Campo obbligatorio'),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          isForeignBorn: yup.boolean(),
          birthPlaceItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          codeIstat: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
          descriptionBirthPlace: yup.string().required('Campo obbligatorio'),
          italian: yup
            .object()
            .nullable()
            .shape({
              municipality: yup
                .object()
                .nullable()
                .shape({})
                .nullable()
                .required('Campo obbligatorio'),
            }),
          foreign: yup.object().shape({}),
          residenceId: yup.string().notRequired().nullable(),
          italianResidence: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .when('residenceId', {
              is: (residenceId: string) =>
                residenceId === ID_RESIDENCE_LIST.ConMadre ||
                residenceId === ID_RESIDENCE_LIST.ConPadre,
              then: yup.object().shape({}).nullable().required('Campo obbligatorio'),
              otherwise: yup.object().shape({}).nullable(),
            }),
          reasonDelay: yup.string().nullable(),
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('reasonDelay', 'Campo obbligatorio', (values) => {
          // controllo se presente motivo ritardo comunicazione
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.reasonDelay && withReasonDelay) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.reasonDelay`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        }),
    });
};

const schemaNASC03 = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime,
  withReasonDelay: boolean
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .nullable()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          birthTime: yup.date().typeError('Data non valida').nullable(),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          birthPlaceItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          codeIstat: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
          descriptionBirthPlace: yup.string().required('Campo obbligatorio'),
          verificationTypeItem: yup.object().nullable().shape({}).nullable(),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().shape({}),
          residenceId: yup.string().notRequired().nullable(),
          reasonChoosingSurname: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          italianResidence: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .when('residenceId', {
              is: (residenceId: string) =>
                residenceId === ID_RESIDENCE_LIST.ConMadre ||
                residenceId === ID_RESIDENCE_LIST.ConPadre,
              then: yup.object().shape({}).nullable().required('Campo obbligatorio'),
              otherwise: yup.object().shape({}).nullable(),
            }),
          reasonDelay: yup.string().nullable(),
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('reasonDelay', 'Campo obbligatorio', (values) => {
          // controllo se presente motivo ritardo comunicazione
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.reasonDelay && withReasonDelay) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.reasonDelay`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        }),
    });
};

const schemaNASC07 = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime,
  withReasonDelay: boolean
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .nullable()
        .shape({
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          name: yup.string().nullable().required('Campo obbligatorio'),
          surname: yup.string().nullable().required('Campo obbligatorio'),
          gender: yup.string().nullable().required('Campo obbligatorio'),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .required('Campo obbligatorio'),
          birthTime: yup.date().typeError('Data non valida').nullable(),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          birthPlaceItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          codeIstat: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
          descriptionBirthPlace: yup.string().required('Campo obbligatorio'),
          reasonChoosingSurname: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .required('Campo obbligatorio'),
          verificationTypeItem: yup.object().nullable().shape({}).nullable(),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().shape({}),
          reasonDelay: yup.string().nullable(),
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isWithTwins && !Boolean(valuesCasted.allTwins)) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('reasonDelay', 'Campo obbligatorio', (values) => {
          // controllo se presente motivo ritardo comunicazione
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.reasonDelay && withReasonDelay) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.reasonDelay`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        }),
    });
};

const schemaNASC03C = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime,
  withReasonDelay: boolean
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .nullable()
        .shape({
          isRequiredFromOtherAccordion: yup.boolean().nullable(),
          isWithTwins: yup.boolean().nullable(),
          allTwins: yup.number().nullable(),
          reasonChoosingSurname: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.object().nullable().shape({}).required('Campo obbligatorio'),
              otherwise: yup.object().nullable().shape({}).nullable(),
            }),
          name: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          surname: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          gender: yup
            .string()
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.string().nullable().required('Campo obbligatorio'),
              otherwise: yup.string().nullable(),
            }),
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .max(
              isDichiarante
                ? dateFormationAct.minus({ years: 18 })
                : dateFormationAct.plus({ day: 1 }),
              isDichiarante
                ? 'Il soggetto deve essere maggiorenne'
                : "La data deve essere minore uguale della data di formazione dell'atto"
            )
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .required('Campo obbligatorio'),
              otherwise: yup.date().typeError('Data non valida').nullable(),
            }),
          birthTime: yup.date().typeError('Data non valida').nullable(),
          conditionsItem: yup.object().nullable().shape({}),
          conditionsItemSignatory: yup.object().nullable().shape({}),
          birthPlaceItem: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
              otherwise: yup.object().nullable().shape({}).nullable(),
            }),
          codeIstat: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .when('isRequiredFromOtherAccordion', {
              is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
              then: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
              otherwise: yup.object().nullable().shape({}).nullable(),
            }),
          descriptionBirthPlace: yup.string().when('isRequiredFromOtherAccordion', {
            is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
            then: yup.string().nullable().required('Campo obbligatorio'),
            otherwise: yup.string().nullable(),
          }),
          verificationTypeItem: yup.object().nullable().shape({}).nullable(),
          isForeignBorn: yup.boolean(),
          italian: yup.object().nullable().shape({}),
          foreign: yup.object().shape({}),
          residenceId: yup.string().notRequired().nullable(),
          italianResidence: yup
            .object()
            .nullable()
            .shape({})
            .nullable()
            .when('residenceId', {
              is: (residenceId: string) =>
                residenceId === ID_RESIDENCE_LIST.ConMadre ||
                residenceId === ID_RESIDENCE_LIST.ConPadre,
              then: yup.object().shape({}).nullable().required('Campo obbligatorio'),
              otherwise: yup.object().shape({}).nullable(),
            }),
          reasonDelay: yup.string().nullable(),
        })
        .test('allTwins', 'Campo obbligatorio', (values) => {
          // controllo gemelli
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.isWithTwins &&
            !Boolean(valuesCasted.allTwins) &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.allTwins`
            );
          }

          return true;
        })
        .test('italian', 'Campo obbligatorio', (values) => {
          // controllo nascita italian
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.isForeignBorn &&
            !valuesCasted.italian?.municipality?.description &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.italian.municipality`
            );
          }

          return true;
        })
        .test('foreign', 'Campo obbligatorio', (values) => {
          // controllo nascita foreign
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            valuesCasted.isForeignBorn &&
            !valuesCasted.foreign?.state?.id &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.foreign.state`
            );
          }

          return true;
        })
        .test('reasonDelay', 'Campo obbligatorio', (values) => {
          // controllo se presente motivo ritardo comunicazione
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.reasonDelay &&
            withReasonDelay &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.reasonDelay`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItem?.id &&
            isDichiarante &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItemSignatory?.id &&
            isFirmatario &&
            !isDichiarante &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        }),
    });
};

const schemaSANIT01 = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup.object().shape({
    generality: yup
      .object()
      .shape({
        name: yup.string().nullable().required('Campo obbligatorio'),
        surname: yup.string().nullable().required('Campo obbligatorio'),
        gender: yup.string().nullable().required('Campo obbligatorio'),
        nationality: yup.object().shape({}).required('Campo obbligatorio'),
        fiscalCode: yup
          .string()
          .notRequired()
          .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
        isMinorDeclarant: yup.boolean().nullable(),
        birthDate: yup
          .date()
          .typeError('Data non valida')
          .nullable()
          .when('isMinorDeclarant', {
            is: (isMinorDeclarant: boolean) => isMinorDeclarant,
            then: yup
              .date()
              .typeError('Data non valida')
              .max(
                dateFormationAct.plus({ day: 1 }),
                "La data deve essere minore uguale della data di formazione dell'atto"
              )
              .required('Campo obbligatorio'),
            otherwise: yup
              .date()
              .typeError('Data non valida')
              .max(
                isDichiarante
                  ? dateFormationAct.minus({ years: 18 })
                  : dateFormationAct.plus({ day: 1 }),
                isDichiarante
                  ? 'Il soggetto deve essere maggiorenne'
                  : "La data deve essere minore uguale della data di formazione dell'atto"
              )
              .required('Campo obbligatorio'),
          }),
        conditionsItem: yup.object().shape({}),
        rolo: yup.string().nullable().required('Campo obbligatorio'),
        isForeignBorn: yup.boolean(),
        italian: yup.object().nullable().shape({}),
        foreign: yup.object().shape({}),
        italianResidence: yup.object().shape({}),
        foreignResidence: yup.object().shape({}),
        typeResidence: yup.string().nullable(),
      })
      .test('conditionsItem', 'Campo obbligatorio', (values) => {
        // controllo Condizioni dichiarante se isDichiarante è true
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (!valuesCasted.conditionsItem?.id && isDichiarante) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.conditionsItem`
          );
        }

        return true;
      })
      .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
        // controllo Condizioni dichiarante se isDichiarante è true
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.conditionsItemSignatory`
          );
        }

        return true;
      })
      .test('italian', 'Campo obbligatorio', (values) => {
        // controllo nascita italian
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.italian.municipality`
          );
        }

        return true;
      })
      .test('foreign', 'Campo obbligatorio', (values) => {
        // controllo nascita foreign
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.foreign.state`
          );
        }

        return true;
      })
      .test('italianResidence', 'Campo obbligatorio', (values) => {
        // controllo residenza italian
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (
          valuesCasted.typeResidence === IdResidence.Italian &&
          !valuesCasted.italianResidence?.municipality?.description
        ) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.italianResidence.municipality`
          );
        }

        return true;
      })
      // .test('foreignResidence', 'Campo obbligatorio', (values) => {
      //   // controllo residenza foreign
      //   const valuesCasted = values as unknown as AccordionGeneralityForm;

      //   if (
      //     valuesCasted.typeResidence === IdResidence.Foreign &&
      //     !valuesCasted.foreignResidence?.municipality?.description
      //   ) {
      //     return new yup.ValidationError(
      //       'Campo obbligatorio',
      //       undefined,
      //       `${keySubject}.generality.foreignResidence.municipality`
      //     );
      //   }

      //   return true;
      // })
      .test('foreignResidence', 'Campo obbligatorio', (values) => {
        // controllo residenza foreign
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (
          valuesCasted.typeResidence === IdResidence.Foreign &&
          !valuesCasted.foreignResidence?.state?.id
        ) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.foreignResidence.state`
          );
        }

        return true;
      }),
  });
};

const schemaPROCUR01 = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup.object().shape({
    generality: yup
      .object()
      .shape({
        name: yup.string().nullable().required('Campo obbligatorio'),
        surname: yup.string().nullable().required('Campo obbligatorio'),
        gender: yup.string().nullable().required('Campo obbligatorio'),
        nationality: yup.object().shape({}).required('Campo obbligatorio'),
        fiscalCode: yup
          .string()
          .notRequired()
          .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
        isMinorDeclarant: yup.boolean().nullable(),
        birthDate: yup
          .date()
          .typeError('Data non valida')
          .nullable()
          .when('isMinorDeclarant', {
            is: (isMinorDeclarant: boolean) => isMinorDeclarant,
            then: yup
              .date()
              .typeError('Data non valida')
              .max(
                dateFormationAct.plus({ day: 1 }),
                "La data deve essere minore uguale della data di formazione dell'atto"
              )
              .required('Campo obbligatorio'),
            otherwise: yup
              .date()
              .typeError('Data non valida')
              .max(
                isDichiarante
                  ? dateFormationAct.minus({ years: 18 })
                  : dateFormationAct.plus({ day: 1 }),
                isDichiarante
                  ? 'Il soggetto deve essere maggiorenne'
                  : "La data deve essere minore uguale della data di formazione dell'atto"
              )
              .required('Campo obbligatorio'),
          }),
        conditionsItem: yup.object().shape({}),
        isForeignBorn: yup.boolean(),
        italian: yup.object().nullable().shape({}),
        foreign: yup.object().shape({}),
        italianResidence: yup.object().shape({}),
        foreignResidence: yup.object().shape({}),
        typeResidence: yup.string().nullable(),
        attorney: yup.string().nullable().required('Campo obbligatorio'),
      })
      .test('conditionsItem', 'Campo obbligatorio', (values) => {
        // controllo Condizioni dichiarante se isDichiarante è true
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (!valuesCasted.conditionsItem?.id && isDichiarante) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.conditionsItem`
          );
        }

        return true;
      })
      .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
        // controllo Condizioni dichiarante se isDichiarante è true
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.conditionsItemSignatory`
          );
        }

        return true;
      })
      .test('italian', 'Campo obbligatorio', (values) => {
        // controllo nascita italian
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (!valuesCasted.isForeignBorn && !valuesCasted.italian?.municipality?.description) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.italian.municipality`
          );
        }

        return true;
      })
      // .test('foreign', 'Campo obbligatorio', (values) => {
      //   // controllo nascita foreign
      //   const valuesCasted = values as unknown as AccordionGeneralityForm;

      //   if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.municipality?.description) {
      //     return new yup.ValidationError(
      //       'Campo obbligatorio',
      //       undefined,
      //       `${keySubject}.generality.foreign.municipality`
      //     );
      //   }

      //   return true;
      // })
      .test('foreign', 'Campo obbligatorio', (values) => {
        // controllo nascita foreign
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (valuesCasted.isForeignBorn && !valuesCasted.foreign?.state?.id) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.foreign.state`
          );
        }

        return true;
      })
      .test('italianResidence', 'Campo obbligatorio', (values) => {
        // controllo residenza italian
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (
          valuesCasted.typeResidence === IdResidence.Italian &&
          !valuesCasted.italianResidence?.municipality?.description
        ) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.italianResidence.municipality`
          );
        }

        return true;
      })
      // .test('foreignResidence', 'Campo obbligatorio', (values) => {
      //   // controllo residenza foreign
      //   const valuesCasted = values as unknown as AccordionGeneralityForm;

      //   if (
      //     valuesCasted.typeResidence === IdResidence.Foreign &&
      //     !valuesCasted.foreignResidence?.municipality?.description
      //   ) {
      //     return new yup.ValidationError(
      //       'Campo obbligatorio',
      //       undefined,
      //       `${keySubject}.generality.foreignResidence.municipality`
      //     );
      //   }

      //   return true;
      // })
      .test('foreignResidence', 'Campo obbligatorio', (values) => {
        // controllo residenza foreign
        const valuesCasted = values as unknown as AccordionGeneralityForm;

        if (
          valuesCasted.typeResidence === IdResidence.Foreign &&
          !valuesCasted.foreignResidence?.state?.id
        ) {
          return new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.generality.foreignResidence.state`
          );
        }

        return true;
      }),
  });
};

const schemaBASEM = () => {
  return yup.object().shape({
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    // italian form
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().shape({}).required('Campo obbligatorio'),
      }),
  });
};

const schemaRICSECONS = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isRequiredFromOtherAccordion: yup.boolean().nullable(),
      documentDateFormation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
          otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
        }),
      documentExtremes: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable().nullable(),
        }),
      nameInstitution: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable().nullable(),
        }),
      // italian form
      italian: yup
        .object()
        .nullable()
        .shape({
          municipality: yup.object().shape({}).nullable(),
        }),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.generality.italian.municipality`
        );
      }

      return true;
    });
};

const schemaMORDATIAU = () => {
  return yup.object().shape({
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    // italian form
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().shape({}).required('Campo obbligatorio'),
      }),
  });
};

const schemaENTEDICHIT = () => {
  return yup.object().shape({
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    // italian form
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().shape({}).required('Campo obbligatorio'),
      }),
  });
};

const schemaENTEDICHIN = () => {
  return yup.object().shape({
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    address: yup.string().nullable().required('Campo obbligatorio'),
    // italian form
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().shape({}).required('Campo obbligatorio'),
      }),
  });
};

const schemaENTEDICHA = () => {
  return yup.object().shape({
    isNotary: yup.boolean().nullable(),
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .when('isNotary', {
        is: (isNotary: boolean) => isNotary,
        then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
        otherwise: yup.date().typeError('Data non valida').nullable(),
      }),
    documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    address: yup.string().nullable(),
    // italian form
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().shape({}).required('Campo obbligatorio'),
      }),
  });
};

const schemaENTEDICHNC = (keySection: string) => {
  return yup
    .object()
    .shape({
      isNotary: yup.boolean().nullable(),
      documentDateFormation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .when('isNotary', {
          is: (isNotary: boolean) => isNotary,
          then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
          otherwise: yup.date().typeError('Data non valida').nullable(),
        }),
      documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
      nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
      address: yup.string().nullable(),
      isForeign: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySection}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySection}.foreign.state`
        );
      }

      return true;
    })
    .test('consulate', 'Campo obbligatorio', (values) => {
      // controllo consolato
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isForeign &&
        !valuesCasted.foreign?.consulate?.id &&
        Number.parseInt(valuesCasted.foreign?.state?.code?.toString() || '', 10) !==
          ID_ITALIAN_NATION
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySection}.foreign.consulate`
        );
      }

      return true;
    });
};

const schemaIMPGEN = () => {
  return yup.object().shape({
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    reason: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaIMPED01 = () => {
  return yup.object().shape({
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    reason: yup.string().nullable().required('Campo obbligatorio'),
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().shape({}).required('Campo obbligatorio'),
      }),
  });
};

const schemaPROVCOM = () => {
  return yup.object().shape({
    // italian form
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().shape({}).required('Campo obbligatorio'),
      }),
  });
};

const schemaPROVCOMC = () => {
  return yup.object().shape({
    isRequiredFromOtherAccordion: yup.boolean().nullable(),
    // italian form
    italian: yup
      .object()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup
          .object()
          .nullable()
          .shape({
            municipality: yup.object().shape({}).required('Campo obbligatorio'),
          }),
        otherwise: yup.object().nullable(),
      }),
  });
};

const schemaRICATTCONS = () => {
  return yup.object().shape({
    isRequiredFromOtherAccordion: yup.boolean().nullable(),
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
        otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
      }),
    documentExtremes: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
    nameInstitution: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
    // italian form
    italian: yup
      .object()
      .nullable()
      .shape({})
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup
          .object()
          .nullable()
          .shape({
            municipality: yup.object().shape({}).required('Campo obbligatorio'),
          }),
        otherwise: yup.object().nullable().shape({}).nullable(),
      }),
  });
};

const schemaCSRA = () => {
  return yup.object().shape({
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    isForeignBorn: yup.boolean(),
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
      }),
    foreign: yup.object().nullable().shape({}),
  });
};

const schemaCSAD = (keySubject: string) => {
  return yup
    .object()
    .shape({
      documentDateFormation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
      nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
      isForeignBorn: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const ENTEDICHS = (keySubject: string) => {
  return yup
    .object()
    .shape({
      documentDateFormation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      judgmentPublicationDate: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      effectiveDateSentence: yup.date().typeError('Data non valida').nullable(),
      documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
      nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
      isForeignBorn: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaTRDATIATIN = (keySubject: string) => {
  return yup
    .object()
    .shape({
      documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
      nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
      isForeignBorn: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
      address: yup.string().nullable().required('Campo obbligatorio'),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaENTEDICHCF = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isRequiredFromOtherAccordion: yup.boolean().nullable(),
      documentDateFormation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
          otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
        }),
      documentExtremes: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable().nullable(),
        }),
      nameInstitution: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable().nullable(),
        }),
      isForeignBorn: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.isForeign &&
        !valuesCasted.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        valuesCasted.isForeign &&
        !valuesCasted.foreign?.state?.value
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaENTEDICHC = (keySubject: string) => {
  return yup
    .object()
    .shape({
      documentDateFormation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
      nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
      isForeignBorn: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaENTEDICHE = () => {
  return yup.object().shape({
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    foreign: yup
      .object()
      .shape({
        state: yup.object().shape({}).required('Campo obbligatorio'),
      })
      .required('Campo obbligatorio'),
    documentDescription: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaENTEDICHIF = () => {
  return yup.object().shape({
    isRequiredFromOtherAccordion: yup.boolean().nullable(),
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
        otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
      }),
    documentExtremes: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
    nameInstitution: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
  });
};

const schemaASSFIG = () => {
  return yup.object().shape({
    isRequiredFromOtherAccordion: yup.boolean().nullable(),
    typeDeclaration: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
    consentReturnedBefore: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
  });
};

const schemaASSRESO = () => {
  return yup.object().shape({
    isRequiredFromOtherAccordion: yup.boolean().nullable(),
    consentReturnedBefore: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
  });
};

const schemaENTEDICHITF = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isRequiredFromOtherAccordion: yup.boolean().nullable(),
      documentDateFormation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
          otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
        }),
      documentExtremes: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable().nullable(),
        }),
      nameInstitution: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable().nullable(),
        }),
      italian: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    });
};

const schemaPUBBLI = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isRequiredFromOtherAccordion: yup.boolean().nullable(),
      dataStart: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
          otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
        }),
      dataEnd: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
          otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
        }),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('dataEnd', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted?.dataStart &&
        valuesCasted?.dataEnd &&
        valuesCasted?.dataStart > valuesCasted?.dataEnd
      ) {
        return new yup.ValidationError(
          'La data deve essere maggiore della Data inizio',
          undefined,
          `${keySubject}.dataEnd`
        );
      }

      return true;
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.isForeign &&
        !valuesCasted.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        valuesCasted.isForeign &&
        !valuesCasted.foreign?.state?.value
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    })
    .test('consulate', 'Campo obbligatorio', (values) => {
      // controllo consolato
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        valuesCasted.isForeign &&
        !valuesCasted.foreign?.consulate?.id &&
        Number.parseInt(valuesCasted.foreign?.state?.code?.toString() || '', 10) !==
          ID_ITALIAN_NATION
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.consulate`
        );
      }

      return true;
    });
};

const schemaIMPGENF = () => {
  return yup.object().shape({
    isRequiredFromOtherAccordion: yup.boolean().nullable(),
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
        otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
      }),
    documentExtremes: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
    nameInstitution: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
    reason: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
  });
};

const schemaIMPED01F = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isRequiredFromOtherAccordion: yup.boolean().nullable(),
      documentDateFormation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
          otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
        }),
      documentExtremes: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable().nullable(),
        }),
      nameInstitution: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable().nullable(),
        }),
      reason: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable().nullable(),
        }),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    });
};

const schemaBASERIC = () => {
  return yup.object().shape({
    certificateReleaseFrom: yup.string().nullable().required('Campo obbligatorio'),
    certificateDate: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .required('Campo obbligatorio'),
    conceivedMonth: yup.string().nullable().required('Campo obbligatorio'),
    isIncestuousSonSentence: yup.boolean().nullable(),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaBASERICNSE = () => {
  return yup.object().shape({
    certificateReleaseFrom: yup.string().nullable().required('Campo obbligatorio'),
    certificateDate: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .required('Campo obbligatorio'),
    conceivedMonth: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaBASERICAU = () => {
  return yup.object().shape({
    documentDateFormation: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().shape({}).nullable().required('Campo obbligatorio'),
      }),
  });
};

const schemaBASERICAUC = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isRequiredFromOtherAccordion: yup.boolean().nullable(),
      documentDateFormation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
          otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
        }),
      documentExtremes: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable().nullable(),
        }),
      nameInstitution: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable().nullable(),
        }),
      // italian form
      italian: yup
        .object()
        .nullable()
        .shape({
          municipality: yup.object().shape({}).nullable(),
        }),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    });
};

const schemaGIURCIT2C = () => {
  return yup.object().shape({
    isRequiredFromOtherAccordion: yup.boolean().nullable(),
    typeRequest: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    dateTranscription: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
        otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
      }),
    dateOath: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
        otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
      }),
    extremesOath: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    dateReport: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
        otherwise: yup.date().typeError('Data non valida').nullable().nullable(),
      }),
    requestProtocol: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaTRATXTBASE = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRANEGASS = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    dateStipulation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
  });
};

const schemaTRATXTBSDE = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    typeRequest: yup.string().nullable().required('Campo obbligatorio'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXTBSDF = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    typeRequest: yup.string().nullable().required('Campo obbligatorio'),
    typeModificationMarriage: yup.string().nullable().required('Campo obbligatorio'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXTUCE = () => {
  return yup.object().shape({
    dataCivilUnion: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    dateTranscription: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .when('typeRequest', {
        is: (typeRequest: string) => typeRequest === IDTypeRequest.Scritta,
        then: yup.date().nullable().typeError('Data non valida').required('Campo obbligatorio'),
        otherwise: yup.date().nullable().typeError('Data non valida'),
      }),
    typeRequest: yup.string().nullable().required('Campo obbligatorio'),
    typeActToTranscribed: yup.string().nullable().required('Campo obbligatorio'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXTUC3 = () => {
  return yup.object().shape({
    dataCivilUnion: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    dateTranscription: yup.date().nullable().typeError('Data non valida'),
    typeRequest: yup.string().nullable().required('Campo obbligatorio'),
    typeActToTranscribed: yup.string().nullable().required('Campo obbligatorio'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXTMTE = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateEvent: yup.date().nullable().required('Campo obbligatorio').typeError('Data non valida'),
      dateTranscription: yup
        .date()
        .nullable()
        .required('Campo obbligatorio')
        .typeError('Data non valida'),
      typeRequest: yup.string().nullable().required('Campo obbligatorio'),

      templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.isForeign &&
        !valuesCasted.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        valuesCasted.isForeign &&
        !valuesCasted.foreign?.state?.value
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaTRATXTMTAE = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateEvent: yup.date().nullable().required('Campo obbligatorio').typeError('Data non valida'),
      templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.isForeign &&
        !valuesCasted.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        valuesCasted.isForeign &&
        !valuesCasted.foreign?.state?.value
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaTRATXTUC2 = () => {
  return yup.object().shape({
    typeRequest: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .when('typeRequest', {
        is: (typeRequest: string) => typeRequest === IDTypeRequest.Scritta,
        then: yup.date().nullable().typeError('Data non valida').required('Campo obbligatorio'),
        otherwise: yup.date().nullable().typeError('Data non valida'),
      }),
    typeActToTranscribed: yup.string().nullable().required('Campo obbligatorio'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXTBASI = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    nameInstituteAbandonedMinor: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXTBASG = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    sentenceRecognition: yup.string().nullable().required('Campo obbligatorio'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXTBASN = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    newSurname: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXBASRP = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    newSurname: yup.string().nullable().required('Campo obbligatorio'),
    typeApplicant: yup.string().nullable().required('Campo obbligatorio'),
    numberHolders: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXTBASV = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    newSurname: yup.string().nullable().required('Campo obbligatorio'),
    applicant: yup.object().shape({}).nullable().required('Campo obbligatorio'),
    typeRequest: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRASCRTISC = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    typeDisappearance: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXBASF = () => {
  return yup.object().shape({
    dateMarriage: yup.date().nullable().required('Campo obbligatorio').typeError('Data non valida'),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXBASS = () => {
  return yup.object().shape({
    dateOutcomeVerification: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    assessmentResult: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXBASC = () => {
  return yup.object().shape({
    assessmentResult: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXBASD = () => {
  return yup.object().shape({
    dateOutcomeVerification: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    assessmentResult: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    summaryStatement: yup.string().nullable().required('Campo obbligatorio'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXBASQ = () => {
  return yup.object().shape({
    dateOutcomeVerification: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXTBASC = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    dateMarriage: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
  });
};

const schemaTRASTXTRIC = () => {
  return yup.object().shape({
    applicant: yup.object().shape({}).nullable().required('Campo obbligatorio'),
    typeRequest: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRATXBASR = () => {
  return yup.object().shape({
    typeRequest: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDGRECUPC = () => {
  return yup.object().shape({
    isRequiredFromOtherAccordion: yup.boolean().nullable(),
    effectiveDate: yup.date().typeError('Data non valida').nullable(),
    effectiveTime: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .when('effectiveDate', {
        is: (effectiveDate: DateTime) =>
          effectiveDate && effectiveDate?.toString() !== 'Invalid Date',
        then: yup.date().nullable().typeError('Data non valida').required('Campo obbligatorio'),
        otherwise: yup.date().nullable().typeError('Data non valida'),
      }),
    reasonRecovery: yup.string().nullable(),
    descriptionsReasonRecovery: yup
      .string()
      .nullable()
      .when('reasonRecovery', {
        is: (reasonRecovery: string) => reasonRecovery === ID_OTHER_REASON,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaTRATXBASRC = () => {
  return yup.object().shape({
    isRequiredFromOtherAccordion: yup.boolean().nullable(),
    typeRequest: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
    dateTranscription: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.date().nullable().typeError('Data non valida').required('Campo obbligatorio'),
        otherwise: yup.date().nullable().typeError('Data non valida'),
      }),
    templateTranscriptionText: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
  });
};

const schemaDGRECUP2C = () => {
  return yup.object().shape({
    isRequiredFromOtherAccordion: yup.boolean().nullable(),
    emergencyRegister: yup
      .object()
      .shape({})
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.object().shape({}).nullable().required('Campo obbligatorio'),
        otherwise: yup.object().shape({}).nullable(),
      }),
    margin: yup
      .string()
      .nullable()
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable().nullable(),
      }),
    effectiveDate: yup
      .date()
      .typeError('Data non valida')
      .when('isRequiredFromOtherAccordion', {
        is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
        then: yup.date().typeError('Data non valida').required('Campo obbligatorio'),
        otherwise: yup.date().typeError('Data non valida').nullable(),
      }),
    effectiveTime: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .when('effectiveDate', {
        is: (effectiveDate: DateTime) =>
          effectiveDate && effectiveDate?.toString() !== 'Invalid Date',
        then: yup.date().nullable().typeError('Data non valida').required('Campo obbligatorio'),
        otherwise: yup.date().nullable().typeError('Data non valida'),
      }),
    reasonRecovery: yup.string().nullable(),
    descriptionsReasonRecovery: yup
      .string()
      .nullable()
      .when('reasonRecovery', {
        is: (reasonRecovery: string) => reasonRecovery === ID_OTHER_REASON,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaTRATXBASRN = () => {
  return yup.object().shape({
    typeRequest: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    applicant: yup.object().shape({}).nullable().required('Campo obbligatorio'),
    changeSurname: yup.string().nullable().required('Campo obbligatorio'),
    newName: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) =>
          changeSurname === IDChangeSurname.nuovoCognomeNome ||
          changeSurname === IDChangeSurname.nuovoNome,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    newSurname: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) =>
          changeSurname === IDChangeSurname.nuovoCognomeNome ||
          changeSurname === IDChangeSurname.nuovoCognome,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    nameAdded: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) => changeSurname === IDChangeSurname.nomeAggiunto,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    surnameAdded: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) => changeSurname === IDChangeSurname.cognomeAggiunto,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaDTGANNMO = () => {
  return yup.object().shape({
    changeSurname: yup.string().nullable().required('Campo obbligatorio'),
    oldSurnameRegistered: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) =>
          changeSurname === IDChangeSurname.nuovoCognomeNome ||
          changeSurname === IDChangeSurname.nuovoCognome,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    newSurnameRegistered: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) =>
          changeSurname === IDChangeSurname.nuovoCognomeNome ||
          changeSurname === IDChangeSurname.nuovoCognome,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    oldNameRegistered: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) =>
          changeSurname === IDChangeSurname.nuovoCognomeNome ||
          changeSurname === IDChangeSurname.nuovoNome,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    newNameRegistered: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) =>
          changeSurname === IDChangeSurname.nuovoCognomeNome ||
          changeSurname === IDChangeSurname.nuovoNome,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaTRATXBASRX = () => {
  return yup.object().shape({
    typeRequest: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    changeSurname: yup.string().nullable().required('Campo obbligatorio'),
    newName: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) =>
          changeSurname === IDChangeSurname.nuovoCognomeNome ||
          changeSurname === IDChangeSurname.nuovoNome,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    newSurname: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) =>
          changeSurname === IDChangeSurname.nuovoCognomeNome ||
          changeSurname === IDChangeSurname.nuovoCognome,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    nameAdded: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) => changeSurname === IDChangeSurname.nomeAggiunto,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    surnameAdded: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) => changeSurname === IDChangeSurname.cognomeAggiunto,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaTRATXTBASRO = () => {
  return yup.object().shape({
    typeRequest: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    changeSurname: yup.string().nullable().required('Campo obbligatorio'),
    newName: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) =>
          changeSurname === IDChangeSurname.nuovoCognomeNome ||
          changeSurname === IDChangeSurname.nuovoNome,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    newSurname: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) =>
          changeSurname === IDChangeSurname.nuovoCognomeNome ||
          changeSurname === IDChangeSurname.nuovoCognome,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    nameAdded: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) => changeSurname === IDChangeSurname.nomeAggiunto,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    surnameAdded: yup
      .string()
      .nullable()
      .when('changeSurname', {
        is: (changeSurname: string) => changeSurname === IDChangeSurname.cognomeAggiunto,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaTRATXTBASF = () => {
  return yup.object().shape({
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRASNASLU = () => {
  return yup.object().shape({
    typeBirthPlace: yup.string().nullable().required('Campo obbligatorio'),
    descriptionBirthPlace: yup
      .string()
      .nullable()
      .when('typeBirthPlace', {
        is: (typeBirthPlace: string) => typeBirthPlace === ID_OTHER_PLACE,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRASDATIAT = () => {
  return yup.object().shape({
    documentDateFormation: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    foreign: yup
      .object()
      .nullable()
      .shape({
        state: yup.object().shape({}).nullable().required('Campo obbligatorio'),
      }),
  });
};

const schemaTRAEDATIAT = (keySubject: string) => {
  return yup
    .object()
    .shape({
      documentDateFormation: yup
        .date()
        .nullable()
        .required('Campo obbligatorio')
        .typeError('Data non valida'),
      documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
      nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
      foreign: yup.object().nullable(),
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo Condizioni dichiarante se isDichiarante è true
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.foreign?.state?.id) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      if (
        Number.parseInt(valuesCasted.foreign?.state?.code?.toString() || '', 10) ===
        ID_ITALIAN_NATION
      ) {
        return new yup.ValidationError(
          'Il valore ITALIA non è ammesso in questo contesto',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaTRASDATIAU = (keySubject: string) => {
  return yup
    .object()
    .shape({
      nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
      isForeign: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    })
    .test('consulate', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isForeign &&
        !valuesCasted.foreign?.consulate?.id &&
        Number.parseInt(valuesCasted.foreign?.state?.code?.toString() || '', 10) !==
          ID_ITALIAN_NATION
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.consulate`
        );
      }

      return true;
    });
};

const schemaTRASDATIA = (keySubject: string) => {
  return yup
    .object()
    .shape({
      nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
      isForeign: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaTRASDATIAC = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isRequiredFromOtherAccordion: yup.boolean().nullable(),
      nameInstitution: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      isForeign: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.isForeign &&
        !valuesCasted.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        valuesCasted.isForeign &&
        !valuesCasted.foreign?.state?.value
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaAUMITITA = () => {
  return yup.object().shape({
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaTRASDATIAV = () => {
  return yup.object().shape({
    nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
    isForeign: yup.boolean(),
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().nullable().shape({}).required('Campo obbligatorio'),
      }),
  });
};

const schemaTRASDATIAVC = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isRequiredFromOtherAccordion: yup.boolean().nullable(),
      nameInstitution: yup
        .string()
        .nullable()
        .when('isRequiredFromOtherAccordion', {
          is: (isRequiredFromOtherAccordion: boolean) => isRequiredFromOtherAccordion,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      isForeign: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // Comune obbligatorio solo quando l'autorità è italiana (isForeign === false)
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.isForeign &&
        !valuesCasted.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // Stato ente obbligatorio solo quando l'autorità è estera (isForeign === true)
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        valuesCasted.isForeign &&
        !valuesCasted.foreign?.state?.value
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaENTEDICHAC = (keySubject: string) => {
  return yup
    .object()
    .shape({
      nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
      documentDateFormation: yup
        .date()
        .nullable()
        .required('Campo obbligatorio')
        .typeError('Data non valida'),
      documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
      isForeign: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    })
    .test('consulate', 'Campo obbligatorio', (values) => {
      // controllo consulate
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isForeign &&
        !valuesCasted.foreign?.consulate?.id &&
        Number.parseInt(valuesCasted.foreign?.state?.code?.toString() || '', 10) !==
          ID_ITALIAN_NATION
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.consulate`
        );
      }

      return true;
    });
};

const schemaTRAEDATIAU = (keySubject: string) => {
  return yup
    .object()
    .shape({
      nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
      foreign: yup.object().nullable(),
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo Condizioni dichiarante se isDichiarante è true
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.foreign?.state?.id) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      if (
        Number.parseInt(valuesCasted.foreign?.state?.code?.toString() || '', 10) ===
        ID_ITALIAN_NATION
      ) {
        return new yup.ValidationError(
          'Il valore ITALIA non è ammesso in questo contesto',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    })
    .test('consulate', 'Campo obbligatorio', (values) => {
      // controllo consulate
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.foreign?.consulate?.id) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.consulate`
        );
      }

      return true;
    });
};

const schemaBASEDECA = (keySection: string) => {
  return yup
    .object()
    .shape({
      isDatePresumed: yup.boolean().nullable(),
      datePresumedStart: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      datePresumedEnd: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      dateDeath: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => !isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      conditionsItem: yup.object().nullable().shape({}).required('Campo obbligatorio'),
      timeDeath: yup.date().nullable().typeError('Data non valida'),
      // .when('isDatePresumed', {
      //   is: (isDatePresumed: boolean) => !isDatePresumed,
      //   then: yup.date().nullable().typeError('Data non valida').required('Campo obbligatorio'),
      //   otherwise: yup.date().nullable().typeError('Data non valida'),
      // }),
      numberAppearing: yup.string().nullable().required('Campo obbligatorio'),
    })
    .test('datePresumedEnd', 'Campo obbligatorio', (values) => {
      // controllo data presunata fine
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.datePresumedStart &&
        valuesCasted.datePresumedStart?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd &&
        valuesCasted.datePresumedEnd?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd < valuesCasted.datePresumedStart
      ) {
        return new yup.ValidationError(
          'La data deve essere maggiore uguale alla Data presunta inizio',
          undefined,
          `${keySection}.datePresumedEnd`
        );
      }

      return true;
    });
};

const schemaBASEDECO = (keySection: string) => {
  return yup
    .object()
    .shape({
      isDatePresumed: yup.boolean().nullable(),
      datePresumedStart: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      datePresumedEnd: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      dateDeath: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => !isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      timeDeath: yup.date().nullable().typeError('Data non valida'),
      // .when('isDatePresumed', {
      //   is: (isDatePresumed: boolean) => !isDatePresumed,
      //   then: yup.date().nullable().typeError('Data non valida').required('Campo obbligatorio'),
      //   otherwise: yup.date().nullable().typeError('Data non valida'),
      // }),
      authority: yup.object().shape({}).nullable().required('Campo obbligatorio'),
      otherAuthority: yup
        .string()
        .nullable()
        .when('authority', {
          is: (authority: ItemValoreDominioAttoVM) => authority?.id === ID_OTHER_AUTHORITY,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      dateCommunication: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .required('Campo obbligatorio')
        .max(
          DateTime.now().plus({ day: 1 }),
          'La data deve essere miniore o uguale della data odierna'
        ),
      typeCommunication: yup.object().shape({}).required('Campo obbligatorio'),
    })
    .test('dateCommunication', 'Campo obbligatorio', (values) => {
      // controllo data presunata fine
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.dateCommunication &&
        valuesCasted.dateCommunication?.toString() !== 'Invalid Date' &&
        valuesCasted.dateDeath &&
        valuesCasted.dateDeath?.toString() !== 'Invalid Date' &&
        valuesCasted.dateDeath > valuesCasted.dateCommunication
      ) {
        return new yup.ValidationError(
          'La data deve essere maggiore uguale alla Data di morte',
          undefined,
          `${keySection}.datePresumedEnd`
        );
      }

      if (
        valuesCasted.dateCommunication &&
        valuesCasted.dateCommunication?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd &&
        valuesCasted.datePresumedEnd?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd > valuesCasted.dateCommunication
      ) {
        return new yup.ValidationError(
          'La data deve essere maggiore uguale alla Data presunta fine',
          undefined,
          `${keySection}.datePresumedEnd`
        );
      }

      return true;
    })
    .test('datePresumedEnd', 'Campo obbligatorio', (values) => {
      // controllo data presunata fine
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.datePresumedStart &&
        valuesCasted.datePresumedStart?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd &&
        valuesCasted.datePresumedEnd?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd < valuesCasted.datePresumedStart
      ) {
        return new yup.ValidationError(
          'La data deve essere maggiore uguale alla Data presunta inizio',
          undefined,
          `${keySection}.datePresumedEnd`
        );
      }

      return true;
    });
};

const schemaBASEDECNID = (keySection: string) => {
  return yup
    .object()
    .shape({
      isDatePresumed: yup.boolean().nullable(),
      datePresumedStart: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      datePresumedEnd: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      dateDeath: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => !isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      timeDeath: yup.date().nullable().typeError('Data non valida'),
    })
    .test('datePresumedEnd', 'Campo obbligatorio', (values) => {
      // controllo data presunata fine
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.datePresumedStart &&
        valuesCasted.datePresumedStart?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd &&
        valuesCasted.datePresumedEnd?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd < valuesCasted.datePresumedStart
      ) {
        return new yup.ValidationError(
          'La data deve essere maggiore uguale alla Data presunta inizio',
          undefined,
          `${keySection}.datePresumedEnd`
        );
      }

      return true;
    });
};

const schemaDATIDECEST = (keySection: string) => {
  return yup
    .object()
    .shape({
      isDatePresumed: yup.boolean().nullable(),
      datePresumedStart: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      datePresumedEnd: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      dateDeath: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => !isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      timeDeath: yup.date().nullable().typeError('Data non valida'),
      isForeign: yup.boolean().nullable(),
      foreign: yup.object().shape({
        state: yup.object().shape({}).required('Campo obbligatorio'),
      }),
    })
    .test('datePresumedEnd', 'Campo obbligatorio', (values) => {
      // controllo data presunata fine
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.datePresumedStart &&
        valuesCasted.datePresumedStart?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd &&
        valuesCasted.datePresumedEnd?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd < valuesCasted.datePresumedStart
      ) {
        return new yup.ValidationError(
          'La data deve essere maggiore uguale alla Data presunta inizio',
          undefined,
          `${keySection}.datePresumedEnd`
        );
      }

      return true;
    });
};

const schemaDATIDECGEN = (keySection: string) => {
  return yup
    .object()
    .shape({
      isDatePresumed: yup.boolean().nullable(),
      datePresumedStart: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      datePresumedEnd: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => isDatePresumed,
          then: yup.date().nullable().typeError('Data non valida').required('Campo obbligatorio'),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      dateDeath: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .when('isDatePresumed', {
          is: (isDatePresumed: boolean) => !isDatePresumed,
          then: yup
            .date()
            .nullable()
            .typeError('Data non valida')
            .required('Campo obbligatorio')
            .max(
              DateTime.now().plus({ day: 1 }),
              'La data deve essere miniore o uguale della data odierna'
            ),
          otherwise: yup.date().nullable().typeError('Data non valida'),
        }),
      timeDeath: yup.date().nullable().typeError('Data non valida'),
      isForeign: yup.boolean().nullable(),
      foreign: yup.object().nullable().shape({}),
      italian: yup.object().nullable().shape({}),
    })
    .test('datePresumedEnd', 'Campo obbligatorio', (values) => {
      // controllo data presunata fine
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.datePresumedStart &&
        valuesCasted.datePresumedStart?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd &&
        valuesCasted.datePresumedEnd?.toString() !== 'Invalid Date' &&
        valuesCasted.datePresumedEnd < valuesCasted.datePresumedStart
      ) {
        return new yup.ValidationError(
          'La data deve essere maggiore uguale alla Data presunta inizio',
          undefined,
          `${keySection}.datePresumedEnd`
        );
      }

      return true;
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySection}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySection}.foreign.state`
        );
      }

      return true;
    });
};

const schemaBASECIT06 = () => {
  return yup.object().shape({
    dateConferralDecree: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .required('Campo obbligatorio'),
    dateNotificationDecree: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .required('Campo obbligatorio'),
    dateRegistrationCourt: yup.date().nullable().typeError('Data non valida'),
    extremesDecree: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaBASECIT07 = () => {
  return yup.object().shape({
    dateConferralDecree: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .required('Campo obbligatorio'),
    dateNotificationDecree: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .required('Campo obbligatorio'),
    extremesDecree: yup.string().nullable().required('Campo obbligatorio'),
    dateRegistrationCourt: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .required('Campo obbligatorio'),
  });
};

const schemaDSCIT = () => {
  return yup.object().shape({
    summaryStatement: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDSCIT10 = () => {
  return yup.object().shape({
    summaryStatement: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    parentCitizenship: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDSCIT9 = () => {
  return yup.object().shape({
    summaryStatement: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    sentenceRecognition: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDSCIT7 = () => {
  return yup.object().shape({
    summaryStatement: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    reasonLossCitizenship: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDSCIT8 = () => {
  return yup.object().shape({
    summaryStatement: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    typeService: yup.string().nullable().required('Campo obbligatorio'),
    nationEmployment: yup.object().shape({}).nullable().required('Campo obbligatorio'),
  });
};

const schemaDSCIT4 = () => {
  return yup.object().shape({
    summaryStatement: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    reasonLossCitizenship: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDSCIT6 = () => {
  return yup.object().shape({
    summaryStatement: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    reasonLossCitizenship: yup.string().nullable().required('Campo obbligatorio'),
    typeResidence: yup.string().nullable().required('Campo obbligatorio'),
    italian: yup
      .object()
      .nullable()
      .shape({})
      .when('typeResidence', {
        is: (typeResidence: string) => typeResidence === ID_OTHER_RESIDENCE,
        then: yup.object().nullable().shape({}).required('Campo obbligatorio'),
        otherwise: yup.object().nullable().shape({}),
      }),
  });
};

const schemaDPIMP = (keySubject: string) => {
  return yup
    .object()
    .shape({
      hiringTiming: yup.string().nullable().required('Campo obbligatorio'),
      documentDateFormation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      documentExtremes: yup.string().nullable().required('Campo obbligatorio'),
      nameInstitution: yup.string().nullable().required('Campo obbligatorio'),
      isForeignBorn: yup.boolean(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaATCOBASE = (keySubject: string, isAtcoDRM?: boolean) => {
  return yup
    .object()
    .shape({
      eventType: yup.string().nullable(),
      actForm: yup
        .object()
        .nullable()
        .shape({
          idAnsc: isAtcoDRM
            ? yup.string().nullable().required('Campo obbligatorio')
            : yup.string().nullable().notRequired(),
          italian: yup
            .object()
            .nullable()
            .shape({
              municipality: yup
                .object()
                .nullable()
                .shape({})
                .nullable()
                .required('Campo obbligatorio'),
            }),
          typeEvent: yup.string().nullable().required('Campo obbligatorio'),
          register: yup.string().nullable().required('Campo obbligatorio'),
          year: yup.string().nullable().required('Campo obbligatorio'),
          volume: yup.string().nullable(),
          portion: yup
            .string()
            .nullable()
            .matches(portionRegex, 'Il campo può essere solo un numero tra 1 e 2'),
          serie: yup.string().nullable(),
          municipalNumber: yup
            .string()
            .nullable()
            .required('Campo obbligatorio')
            .matches(regexOnlyNumber, 'Campo numerico'),
        }),
    })
    .test(serieFunction)
    .test('volume', 'Campo obbligatorio', (values) => {
      // controllo volume
      const valuesCasted = values as unknown as SectionVM;

      if (isAtcoDRM) {
        return true;
      }

      const isMilano =
        valuesCasted.actForm?.italian?.municipality?.description?.toLocaleLowerCase() === 'milano';
      const isCartaceo = values.typeAct === CodeTypeAct.attoCartaceo;

      if (!valuesCasted.actForm?.volume && isMilano && isCartaceo) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.volume`
        );
      }

      if (
        valuesCasted.actForm?.volume &&
        isMilano &&
        !regVolRegex.test(valuesCasted.actForm?.volume) &&
        isCartaceo
      ) {
        return new yup.ValidationError(
          'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
          undefined,
          `${keySubject}.actForm.volume`
        );
      }

      return true;
    });
};

const schemaATCOFA = (keySubject: string) => {
  return yup
    .object()
    .shape({
      eventType: yup.string().nullable(),
      typeAct: yup.string().nullable().notRequired(),
      actForm: yup
        .object()
        .nullable()
        .when('typeAct', (typeAct, formValidationSchema) => {
          if (typeAct === CodeTypeAct.attoCartaceo) {
            return formValidationSchema.shape({
              idAnsc: yup.string().nullable().notRequired(),
              italian: yup
                .object()
                .nullable()
                .shape({
                  municipality: yup
                    .object()
                    .nullable()
                    .shape({})
                    .nullable()
                    .required('Campo obbligatorio'),
                }),
              typeEvent: yup.string().nullable().required('Campo obbligatorio'),
              register: yup.string().nullable().required('Campo obbligatorio'),
              year: yup.string().nullable().required('Campo obbligatorio'),
              volume: yup.string().nullable(),
              portion: yup
                .string()
                .nullable()
                .required('Campo obbligatorio')
                .matches(portionRegex, 'Il campo può essere solo un numero tra 1 e 2'),
              serie: yup.string().nullable(),
              municipalNumber: yup
                .string()
                .nullable()
                .required('Campo obbligatorio')
                .matches(regexOnlyNumber, 'Campo numerico'),
            });
          }

          if (typeAct === CodeTypeAct.attoDigitale) {
            return formValidationSchema.shape({
              idAnsc: yup.string().nullable().required('Campo obbligatorio'),
              italian: yup
                .object()
                .nullable()
                .shape({
                  municipality: yup
                    .object()
                    .nullable()
                    .shape({})
                    .nullable()
                    .required('Campo obbligatorio'),
                }),
              typeEvent: yup.string().nullable().required('Campo obbligatorio'),
              register: yup.string().nullable().required('Campo obbligatorio'),
              year: yup.string().nullable().required('Campo obbligatorio'),
              portion: yup
                .string()
                .nullable()
                .matches(portionRegex, 'Il campo può essere solo un numero tra 1 e 2'),
              serie: yup.string().nullable(),
              municipalNumber: yup
                .string()
                .nullable()
                .required('Campo obbligatorio')
                .matches(regexOnlyNumber, 'Campo numerico'),
            });
          }

          return formValidationSchema.shape({});
        }),
    })
    .test(serieFunction)
    .test('volume', 'Campo obbligatorio', (values) => {
      // controllo volume
      const valuesCasted = values as unknown as SectionVM;

      const isMilano =
        valuesCasted.actForm?.italian?.municipality?.description?.toLocaleLowerCase() === 'milano';
      const isCartaceo = values.typeAct === CodeTypeAct.attoCartaceo;

      if (!valuesCasted.actForm?.volume && isMilano && isCartaceo) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.volume`
        );
      }

      if (
        valuesCasted.actForm?.volume &&
        isMilano &&
        !regVolRegex.test(valuesCasted.actForm?.volume) &&
        isCartaceo
      ) {
        return new yup.ValidationError(
          'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
          undefined,
          `${keySubject}.actForm.volume`
        );
      }

      return true;
    });
};

const schemaATCOBASECP = (keySubject: string) => {
  return yup
    .object()
    .shape({
      eventType: yup.string().nullable(),
      isRequiredFromOtherAccordion: yup.boolean().nullable(),
      actForm: yup
        .object()
        .nullable()
        .shape({
          idAnsc: yup.string().nullable().notRequired(),
          italian: yup
            .object()
            .nullable()
            .shape({
              municipality: yup.object().nullable().shape({}),
            }),
          typeEvent: yup.string().nullable(),
          register: yup.string().nullable(),
          year: yup.string().nullable(),
          volume: yup.string().nullable(),
          portion: yup
            .string()
            .nullable()
            .matches(portionRegex, 'Il campo può essere solo un numero tra 1 e 2'),
          serie: yup.string().nullable(),
          municipalNumber: yup.string().nullable().matches(regexOnlyNumber, 'Campo numerico'),
          presentationDocument: yup.string().nullable(),
          dateAct: yup.date().typeError('Data non valida').nullable(),
        }),
    })
    .test(serieFunction)
    .test('volume', 'Campo obbligatorio', (values) => {
      // controllo volume
      const valuesCasted = values as unknown as SectionVM;

      const isMilano =
        valuesCasted.actForm?.italian?.municipality?.description?.toLocaleLowerCase() === 'milano';
      const isCartaceo = values.typeAct === CodeTypeAct.attoCartaceo;

      if (!valuesCasted.actForm?.volume && isMilano && isCartaceo) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.volume`
        );
      }

      if (
        valuesCasted.actForm?.volume &&
        isMilano &&
        !regVolRegex.test(valuesCasted.actForm?.volume) &&
        isCartaceo
      ) {
        return new yup.ValidationError(
          'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
          undefined,
          `${keySubject}.actForm.volume`
        );
      }

      return true;
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.actForm?.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.italian.municipality`
        );
      }

      return true;
    })
    .test('typeEvent', 'Campo obbligatorio', (values) => {
      // controllo typeEvent
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isRequiredFromOtherAccordion && !valuesCasted.actForm?.typeEvent) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.typeEvent`
        );
      }

      return true;
    })
    .test('register', 'Campo obbligatorio', (values) => {
      // controllo register
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isRequiredFromOtherAccordion && !valuesCasted.actForm?.register) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.register`
        );
      }

      return true;
    })
    .test('year', 'Campo obbligatorio', (values) => {
      // controllo year
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isRequiredFromOtherAccordion && !valuesCasted.actForm?.year) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.year`
        );
      }

      return true;
    })
    .test('municipalNumber', 'Campo obbligatorio', (values) => {
      // controllo municipalNumber
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isRequiredFromOtherAccordion && !valuesCasted.actForm?.municipalNumber) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.municipalNumber`
        );
      }

      return true;
    });
  // .test('dateAct', 'Campo obbligatorio', (values) => {
  //   // controllo dateAct
  //   const valuesCasted = values as unknown as SectionVM;

  //   if (
  //     valuesCasted.isRequiredFromOtherAccordion &&
  //     (!valuesCasted.actForm?.dateAct ||
  //       valuesCasted.actForm?.dateAct?.toString() === 'Invalid Date')
  //   ) {
  //     return new yup.ValidationError(
  //       'Campo obbligatorio',
  //       undefined,
  //       `${keySubject}.actForm.dateAct`
  //     );
  //   }

  //   return true;
  // });
};

const schemaATCOBASEC = (keySubject: string) => {
  return yup
    .object()
    .shape({
      eventType: yup.string().nullable(),
      isRequiredFromOtherAccordion: yup.boolean().nullable(),
      actForm: yup
        .object()
        .nullable()
        .shape({
          idAnsc: yup.string().nullable().notRequired(),
          italian: yup.object().nullable().shape({}),
          typeEvent: yup.string().nullable(),
          register: yup.string().nullable(),
          year: yup.string().nullable(),
          volume: yup.string().nullable(),
          portion: yup
            .string()
            .nullable()
            .matches(portionRegex, 'Il campo può essere solo un numero tra 1 e 2'),
          serie: yup.string().nullable(),
          municipalNumber: yup.string().nullable().matches(regexOnlyNumber, 'Campo numerico'),
          dateAct: yup.date().typeError('Data non valida').nullable(),
        }),
    })
    .test(serieFunction)
    .test('volume', 'Campo obbligatorio', (values) => {
      // controllo volume
      const valuesCasted = values as unknown as SectionVM;

      const isMilano =
        valuesCasted.actForm?.italian?.municipality?.description?.toLocaleLowerCase() === 'milano';
      const isCartaceo = values.typeAct === CodeTypeAct.attoCartaceo;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.actForm?.volume &&
        isMilano &&
        isCartaceo
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.volume`
        );
      }

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        valuesCasted.actForm?.volume &&
        isMilano &&
        !regVolRegex.test(valuesCasted.actForm?.volume) &&
        isCartaceo
      ) {
        return new yup.ValidationError(
          'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
          undefined,
          `${keySubject}.actForm.volume`
        );
      }

      return true;
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (
        valuesCasted.isRequiredFromOtherAccordion &&
        !valuesCasted.actForm?.italian?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.italian.municipality`
        );
      }

      return true;
    })
    .test('typeEvent', 'Campo obbligatorio', (values) => {
      // controllo typeEvent
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isRequiredFromOtherAccordion && !valuesCasted.actForm?.typeEvent) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.typeEvent`
        );
      }

      return true;
    })
    .test('register', 'Campo obbligatorio', (values) => {
      // controllo register
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isRequiredFromOtherAccordion && !valuesCasted.actForm?.register) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.register`
        );
      }

      return true;
    })
    .test('year', 'Campo obbligatorio', (values) => {
      // controllo year
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isRequiredFromOtherAccordion && !valuesCasted.actForm?.year) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.year`
        );
      }

      return true;
    })
    .test('municipalNumber', 'Campo obbligatorio', (values) => {
      // controllo municipalNumber
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isRequiredFromOtherAccordion && !valuesCasted.actForm?.municipalNumber) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.actForm.municipalNumber`
        );
      }

      return true;
    });
};

const schemaATCOFAC = (keySubject: string) => {
  return yup
    .object()
    .shape({
      eventType: yup.string().nullable(),
      typeAct: yup.string().nullable().notRequired(),
      isRequiredFromOtherAccordion: yup.boolean().nullable(),
      actForm: yup.object().nullable(),
    })
    .test('actForm-multi-error', function (values) {
      const v = values as unknown as SectionVM;
      const errors: yup.ValidationError[] = [];

      if (!v.isRequiredFromOtherAccordion) return true;

      const isCartaceo = v.typeAct === CodeTypeAct.attoCartaceo;
      const isDigitale = v.typeAct === CodeTypeAct.attoDigitale;

      if (!isCartaceo && !isDigitale) return true;

      const actForm = v.actForm as ActFormVM;

      if (!actForm) {
        errors.push(
          new yup.ValidationError('Campo obbligatorio', undefined, `${keySubject}.actForm`)
        );
      } else {
        if (!actForm.typeEvent) {
          errors.push(
            new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.actForm.typeEvent`
            )
          );
        }

        if (!actForm.register) {
          errors.push(
            new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.actForm.register`
            )
          );
        }

        if (!actForm.year) {
          errors.push(
            new yup.ValidationError('Campo obbligatorio', undefined, `${keySubject}.actForm.year`)
          );
        }

        if (!actForm.municipalNumber) {
          errors.push(
            new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.actForm.municipalNumber`
            )
          );
        } else if (!regrexOnlyNumberWithoutGlobal.test(actForm.municipalNumber)) {
          errors.push(
            new yup.ValidationError(
              'Campo numerico',
              undefined,
              `${keySubject}.actForm.municipalNumber`
            )
          );
        }

        if (!actForm.italian?.municipality) {
          errors.push(
            new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.actForm.italian.municipality`
            )
          );
        }

        if (isCartaceo) {
          if (!actForm.portion) {
            errors.push(
              new yup.ValidationError(
                'Campo obbligatorio',
                undefined,
                `${keySubject}.actForm.portion`
              )
            );
          } else if (!portionRegexWithoutGlobal.test(actForm.portion)) {
            errors.push(
              new yup.ValidationError(
                'Il campo può essere solo un numero tra 1 e 2',
                undefined,
                `${keySubject}.actForm.portion`
              )
            );
          }
        } else if (actForm.portion && !portionRegexWithoutGlobal.test(actForm.portion)) {
          errors.push(
            new yup.ValidationError(
              'Il campo può essere solo un numero tra 1 e 2',
              undefined,
              `${keySubject}.actForm.portion`
            )
          );
        }

        if (isDigitale && !actForm.idAnsc) {
          errors.push(
            new yup.ValidationError('Campo obbligatorio', undefined, `${keySubject}.actForm.idAnsc`)
          );
        }
      }

      if (errors.length > 0) {
        throw new yup.ValidationError(errors);
      }

      return true;
    })
    .test(serieFunction)
    .test('volume', function (values) {
      const v = values as unknown as SectionVM;
      const errors: yup.ValidationError[] = [];

      const isMilano =
        v.actForm?.italian?.municipality?.description?.toLocaleLowerCase() === 'milano';
      const isCartaceo = v.typeAct === CodeTypeAct.attoCartaceo;

      if (v.isRequiredFromOtherAccordion && isCartaceo && isMilano && !v.actForm?.volume) {
        errors.push(
          new yup.ValidationError('Campo obbligatorio', undefined, `${keySubject}.actForm.volume`)
        );
      }

      if (
        v.isRequiredFromOtherAccordion &&
        isCartaceo &&
        isMilano &&
        v.actForm?.volume &&
        !regVolRegex.test(v.actForm.volume)
      ) {
        errors.push(
          new yup.ValidationError(
            'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
            undefined,
            `${keySubject}.actForm.volume`
          )
        );
      }

      if (errors.length > 0) {
        throw new yup.ValidationError(errors);
      }

      return true;
    });
};

const schemaCITLEG = () => {
  return yup.object().shape({
    numberLawCitizenship: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaAtcoBaseF = (keySubject: string) => {
  return yup
    .object()
    .shape({
      eventType: yup.string().nullable(),
      actForm: yup
        .object()
        .nullable()
        .shape({
          idAnsc: yup.string().nullable().notRequired(),
          italian: yup.object().nullable().shape({}),
          typeEvent: yup.string().nullable(),
          register: yup.string().nullable(),
          year: yup.string().nullable(),
          volume: yup.string().nullable(),
          portion: yup
            .string()
            .nullable()
            .matches(portionRegex, 'Il campo può essere solo un numero tra 1 e 2'),
          serie: yup.string().nullable(),
          municipalNumber: yup.string().nullable().matches(regexOnlyNumber, 'Campo numerico'),
        }),
    })
    .test(serieFunction)
    .test('', 'Campi obbligatorio', (values) => {
      // controlli
      const valuesCasted = values as unknown as SectionVM;

      const valuesMomentary = { ...(values || {}), ...clearSectionMomentary() };

      const hasFields = !isObjectEmpty((valuesMomentary || {}) as Record<string, unknown>);

      const errors = [];

      if (hasFields && !valuesCasted.actForm?.italian?.municipality?.description) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.actForm.italian.municipality`
          )
        );
      }

      if (hasFields && !valuesCasted.actForm?.typeEvent) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.actForm.typeEvent`
          )
        );
      }

      if (hasFields && !valuesCasted.actForm?.register) {
        errors.push(
          new yup.ValidationError('Campo obbligatorio', undefined, `${keySubject}.actForm.register`)
        );
      }

      if (hasFields && !valuesCasted.actForm?.year) {
        errors.push(
          new yup.ValidationError('Campo obbligatorio', undefined, `${keySubject}.actForm.year`)
        );
      }

      if (hasFields && !valuesCasted.actForm?.municipalNumber) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.actForm.municipalNumber`
          )
        );
      }

      // if (
      //   hasFields &&
      //   (!valuesCasted.actForm?.dateAct ||
      //     valuesCasted.actForm?.dateAct?.toString() === 'Invalid Date')
      // ) {
      //   errors.push(
      //     new yup.ValidationError('Campo obbligatorio', undefined, `${keySubject}.actForm.dateAct`)
      //   );
      // }

      if (errors.length === 0) {
        return true;
      }

      return new yup.ValidationError(errors);
    });
};

const schemaCERTIF01F = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateIncorporationCertificate: yup.date().typeError('Data non valida').nullable(),
      italian: yup.object().nullable().shape({}),
    })
    .test('', 'Campi obbligatorio', (values) => {
      // controlli
      const valuesCasted = values as unknown as SectionVM;

      const valuesMomentary = { ...(values || {}), ...clearSectionMomentary() };

      const hasFields = !isObjectEmpty((valuesMomentary || {}) as Record<string, unknown>);

      const errors = [];

      if (hasFields && !valuesCasted?.italian?.municipality?.description) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.italian.municipality`
          )
        );
      }

      if (
        hasFields &&
        (!valuesCasted.dateIncorporationCertificate ||
          valuesCasted.dateIncorporationCertificate?.toString() === 'Invalid Date')
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            `${keySubject}.dateIncorporationCertificate`
          )
        );
      }

      if (errors.length === 0) {
        return true;
      }

      return new yup.ValidationError(errors);
    });
};

const schemaINTEGRAZ01F = () => {
  return yup.object().shape({
    surnameEditor: yup.string().nullable(),
    nameEditor: yup.string().nullable(),
    gender: yup.string().nullable(),
    timeDocumentFormation: yup.date().typeError('Data non valida').nullable(),
  });
  // .test('', 'Campi obbligatorio', (values) => {
  //   // controlli
  //   const valuesCasted = values as unknown as SectionVM;

  //   const valuesMomentary = { ...(values || {}), ...clearSectionMomentary() };

  //   const hasFields = !isObjectEmpty((valuesMomentary || {}) as Record<string, unknown>);

  //   const errors = [];

  //   if (hasFields && !valuesCasted.surnameEditor) {
  //     errors.push(
  //       new yup.ValidationError('Campo obbligatorio', undefined, `${keySubject}.surnameEditor`)
  //     );
  //   }

  //   if (hasFields && !valuesCasted.nameEditor) {
  //     errors.push(
  //       new yup.ValidationError('Campo obbligatorio', undefined, `${keySubject}.nameEditor`)
  //     );
  //   }

  //   if (hasFields && !valuesCasted.gender) {
  //     errors.push(
  //       new yup.ValidationError('Campo obbligatorio', undefined, `${keySubject}.gender`)
  //     );
  //   }

  //   if (
  //     hasFields &&
  //     (!valuesCasted.timeDocumentFormation ||
  //       valuesCasted.timeDocumentFormation?.toString() === 'Invalid Date')
  //   ) {
  //     errors.push(
  //       new yup.ValidationError(
  //         'Campo obbligatorio',
  //         undefined,
  //         `${keySubject}.timeDocumentFormation`
  //       )
  //     );
  //   }

  //   if (errors.length === 0) {
  //     return true;
  //   }

  //   return new yup.ValidationError(errors);
  // });
};

const schemaCOMPLIB01 = () => {
  return yup.object().shape({
    actComposition: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDGRECUP = () => {
  return yup.object().shape({
    effectiveDate: yup.date().typeError('Data non valida').nullable(),
    effectiveTime: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .when('effectiveDate', {
        is: (effectiveDate: DateTime) =>
          effectiveDate && effectiveDate?.toString() !== 'Invalid Date',
        then: yup.date().nullable().typeError('Data non valida').required('Campo obbligatorio'),
        otherwise: yup.date().nullable().typeError('Data non valida'),
      }),
    reasonRecovery: yup.string().nullable(),
    descriptionsReasonRecovery: yup
      .string()
      .nullable()
      .when('reasonRecovery', {
        is: (reasonRecovery: string) => reasonRecovery === ID_OTHER_REASON,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaDGRECUP2 = () => {
  return yup.object().shape({
    emergencyRegister: yup.object().shape({}).nullable().required('Campo obbligatorio'),
    margin: yup.string().nullable().required('Campo obbligatorio'),
    effectiveDate: yup.date().typeError('Data non valida').required('Campo obbligatorio'),
    effectiveTime: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .when('effectiveDate', {
        is: (effectiveDate: DateTime) =>
          effectiveDate && effectiveDate?.toString() !== 'Invalid Date',
        then: yup.date().nullable().typeError('Data non valida').required('Campo obbligatorio'),
        otherwise: yup.date().nullable().typeError('Data non valida'),
      }),
    reasonRecovery: yup.string().nullable(),
    descriptionsReasonRecovery: yup
      .string()
      .nullable()
      .when('reasonRecovery', {
        is: (reasonRecovery: string) => reasonRecovery === ID_OTHER_REASON,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaDSCIT2 = () => {
  return yup.object().shape({
    summaryStatement: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaRICDGCSAS = () => {
  return yup.object().shape({
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    typologyConsent: yup.string().nullable().required('Campo obbligatorio'),
    typeDeclaration: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaRICDGAS = () => {
  return yup.object().shape({
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    typeDeclaration: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaRICDGP = () => {
  return yup.object().shape({
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaRICCONS = () => {
  return yup.object().shape({
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    dateCommunication: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .required('Campo obbligatorio'),
  });
};

const schemaRICASSE = () => {
  return yup.object().shape({
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    parentRecognition: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaRICDGCS = () => {
  return yup.object().shape({
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    typologyConsent: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaRICDGCSASC = () => {
  return yup.object().shape({
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    typologyConsent: yup.string().nullable().required('Campo obbligatorio'),
    typeDeclaration: yup.string().nullable().required('Campo obbligatorio'),
    changeSurname: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaRICDGCSSE = () => {
  return yup.object().shape({
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    typologyConsent: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDICHNNOCON = () => {
  return yup.object().shape({
    reasonLackConsent: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaRICGENIT = () => {
  return yup.object().shape({
    parentRecognition: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaESIACSI = () => {
  return yup.object().shape({
    dateMeasureMayor: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    assessmentResult: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaESIACSI2 = () => {
  return yup.object().shape({
    dateEffectiveMeasure: yup.date().typeError('Data non valida').nullable(),
    dateMeasureMayor: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    assessmentResult: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .nullable()
      .required('Campo obbligatorio')
      .typeError('Data non valida'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaGIURCIT = () => {
  return yup.object().shape({
    dateOath: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    extremesOath: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDSNAS01 = () => {
  return yup.object().shape({
    changeSurname: yup.string().nullable().required('Campo obbligatorio'),
    newSurname: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDSNAS02 = () => {
  return yup.object().shape({
    changeSurname: yup.string().nullable().required('Campo obbligatorio'),
    newSurname: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    parentRecognition: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDSNAS03 = () => {
  return yup.object().shape({
    changeSurname: yup.string().nullable().required('Campo obbligatorio'),
    oldSurname: yup.string().nullable().required('Campo obbligatorio'),
    newSurname: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    dateChangeSurname: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    typeMutation: yup.string().nullable().required('Campo obbligatorio'),
    parentChangedSurname: yup.string().nullable().required('Campo obbligatorio'),
    measureExchangeSurname: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDSNAS04 = () => {
  return yup.object().shape({
    changeSurname: yup.string().nullable().required('Campo obbligatorio'),
    oldSurname: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    typeMutation: yup.string().nullable().required('Campo obbligatorio'),
    parentChangedSurname: yup.string().nullable().required('Campo obbligatorio'),
    measureExchangeSurname: yup.string().nullable().required('Campo obbligatorio'),
    newSurname: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaDSNAS05 = () => {
  return yup.object().shape({
    changeSurname: yup.string().nullable().required('Campo obbligatorio'),
    newSurname: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    parentRecognition: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
  });
};

const schemaUCDTSP = () => {
  return yup.object().shape({
    dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
    genericImpediment: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaUCDTSP03 = () => {
  return yup.object().shape({
    dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
    genericImpediment: yup.string().nullable().required('Campo obbligatorio'),
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
      }),
    place: yup.string().nullable().required('Campo obbligatorio'),
    address: yup.string().nullable().required('Campo obbligatorio'),
    houseNumber: yup.string().nullable().required('Campo obbligatorio'),
    impedimentUnitedCivilly: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaUCDTSP05 = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
      numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
      place: yup.string().nullable().required('Campo obbligatorio'),
      address: yup.string().nullable().required('Campo obbligatorio'),
      houseNumber: yup.string().nullable().required('Campo obbligatorio'),
      genericImpediment: yup.string().nullable().required('Campo obbligatorio'),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaMADTSP = () => {
  return yup.object().shape({
    dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
    publication: yup.string().nullable().required('Campo obbligatorio'),
    ageDefect: yup.string().nullable().required('Campo obbligatorio'),
    relationship: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    propertyRegime: yup.string().nullable().notRequired(),
    minorWifeAssistance: yup
      .string()
      .nullable()
      .when(['ageDefect', 'propertyRegime'], {
        is: (ageDefect: string, propertyRegime: string) =>
          propertyRegime === '2' && (ageDefect === '3' || ageDefect === '4'),
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    minorGroomAssistance: yup
      .string()
      .nullable()
      .when(['ageDefect', 'propertyRegime'], {
        is: (ageDefect: string, propertyRegime: string) =>
          propertyRegime === '2' && (ageDefect === '2' || ageDefect === '4'),
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaMADTSP8 = () => {
  return yup.object().shape({
    dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
    ageDefect: yup.string().nullable().required('Campo obbligatorio'),
    relationship: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    propertyRegime: yup.string().nullable().notRequired(),
    minorWifeAssistance: yup
      .string()
      .nullable()
      .when(['ageDefect', 'propertyRegime'], {
        is: (ageDefect: string, propertyRegime: string) =>
          propertyRegime === '2' && (ageDefect === '3' || ageDefect === '4'),
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    minorGroomAssistance: yup
      .string()
      .nullable()
      .when(['ageDefect', 'propertyRegime'], {
        is: (ageDefect: string, propertyRegime: string) =>
          propertyRegime === '2' && (ageDefect === '2' || ageDefect === '4'),
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaMADTSP9 = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      publication: yup.string().nullable().required('Campo obbligatorio'),
      ageDefect: yup.string().nullable().required('Campo obbligatorio'),
      relationship: yup.string().nullable().required('Campo obbligatorio'),
      presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
      propertyRegime: yup.string().nullable().notRequired(),
      minorWifeAssistance: yup
        .string()
        .nullable()
        .when(['ageDefect', 'propertyRegime'], {
          is: (ageDefect: string, propertyRegime: string) =>
            propertyRegime === '2' && (ageDefect === '3' || ageDefect === '4'),
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      minorGroomAssistance: yup
        .string()
        .nullable()
        .when(['ageDefect', 'propertyRegime'], {
          is: (ageDefect: string, propertyRegime: string) =>
            propertyRegime === '2' && (ageDefect === '2' || ageDefect === '4'),
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      place: yup.string().nullable().required('Campo obbligatorio'),
      declarant: yup.string().nullable().required('Campo obbligatorio'),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaMADTSP5 = () => {
  return yup.object().shape({
    dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
    publication: yup.string().nullable().required('Campo obbligatorio'),
    ageDefect: yup.string().nullable().required('Campo obbligatorio'),
    relationship: yup.string().nullable().required('Campo obbligatorio'),
    typologyConsent: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    propertyRegime: yup.string().nullable().notRequired(),
    minorWifeAssistance: yup
      .string()
      .nullable()
      .when(['ageDefect', 'propertyRegime'], {
        is: (ageDefect: string, propertyRegime: string) =>
          propertyRegime === '2' && (ageDefect === '3' || ageDefect === '4'),
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    minorGroomAssistance: yup
      .string()
      .nullable()
      .when(['ageDefect', 'propertyRegime'], {
        is: (ageDefect: string, propertyRegime: string) =>
          propertyRegime === '2' && (ageDefect === '2' || ageDefect === '4'),
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
  });
};

const schemaMADTSP2 = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
      publication: yup.string().nullable().required('Campo obbligatorio'),
      ageDefect: yup.string().nullable().required('Campo obbligatorio'),
      relationship: yup.string().nullable().required('Campo obbligatorio'),
      presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
      ufcDelegate: yup.string().nullable().required('Campo obbligatorio'),
      reason: yup.string().nullable().required('Campo obbligatorio'),
      propertyRegime: yup.string().nullable().notRequired(),
      minorWifeAssistance: yup
        .string()
        .nullable()
        .when(['ageDefect', 'propertyRegime'], {
          is: (ageDefect: string, propertyRegime: string) =>
            propertyRegime === '2' && (ageDefect === '3' || ageDefect === '4'),
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      minorGroomAssistance: yup
        .string()
        .nullable()
        .when(['ageDefect', 'propertyRegime'], {
          is: (ageDefect: string, propertyRegime: string) =>
            propertyRegime === '2' && (ageDefect === '2' || ageDefect === '4'),
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      italianDelegating: yup.object().nullable().shape({}),
      foreignDelegating: yup.object().nullable().shape({}),
      dataDelegate: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
    })
    .test('italianDelegating', 'Campo obbligatorio', (values) => {
      const valuesCasted = values as unknown as SectionVM;

      if (
        !valuesCasted.isForeignDelegating &&
        !valuesCasted.italianDelegating?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italianDelegating.municipality`
        );
      }

      return true;
    })
    .test('foreignDelegating', 'Campo obbligatorio', (values) => {
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeignDelegating && !valuesCasted.foreignDelegating?.state?.id) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreignDelegating.state`
        );
      }

      return true;
    });
};

const schemaMADTSP3 = () => {
  return yup.object().shape({
    dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
    publication: yup.string().nullable().required('Campo obbligatorio'),
    ageDefect: yup.string().nullable().required('Campo obbligatorio'),
    relationship: yup.string().nullable().required('Campo obbligatorio'),
    place: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    propertyRegime: yup.string().nullable().notRequired(),
    minorWifeAssistance: yup
      .string()
      .nullable()
      .when(['ageDefect', 'propertyRegime'], {
        is: (ageDefect: string, propertyRegime: string) =>
          propertyRegime === '2' && (ageDefect === '3' || ageDefect === '4'),
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    minorGroomAssistance: yup
      .string()
      .nullable()
      .when(['ageDefect', 'propertyRegime'], {
        is: (ageDefect: string, propertyRegime: string) =>
          propertyRegime === '2' && (ageDefect === '2' || ageDefect === '4'),
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    address: yup.string().nullable().required('Campo obbligatorio'),
    houseNumber: yup.string().nullable().required('Campo obbligatorio'),
    impedimentUnitedCivilly: yup.string().nullable().required('Campo obbligatorio'),
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().shape({}).required('Campo obbligatorio'),
      }),
  });
};

const schemaMADTSP4 = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
      publication: yup.string().nullable().required('Campo obbligatorio'),
      ageDefect: yup.string().nullable().required('Campo obbligatorio'),
      relationship: yup.string().nullable().required('Campo obbligatorio'),
      presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
      ufcDelegate: yup.string().nullable().required('Campo obbligatorio'),
      reason: yup.string().nullable().required('Campo obbligatorio'),
      propertyRegime: yup.string().nullable().notRequired(),
      minorWifeAssistance: yup
        .string()
        .nullable()
        .when(['ageDefect', 'propertyRegime'], {
          is: (ageDefect: string, propertyRegime: string) =>
            propertyRegime === '2' && (ageDefect === '3' || ageDefect === '4'),
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      minorGroomAssistance: yup
        .string()
        .nullable()
        .when(['ageDefect', 'propertyRegime'], {
          is: (ageDefect: string, propertyRegime: string) =>
            propertyRegime === '2' && (ageDefect === '2' || ageDefect === '4'),
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      impedimentUnitedCivilly: yup.string().nullable().required('Campo obbligatorio'),
      address: yup.string().nullable().required('Campo obbligatorio'),
      houseNumber: yup.string().nullable().required('Campo obbligatorio'),
      italianDelegating: yup.object().nullable().shape({}),
      foreignDelegating: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    })
    .test('italianDelegating', 'Campo obbligatorio', (values) => {
      const valuesCasted = values as unknown as SectionVM;

      if (
        !valuesCasted.isForeignDelegating &&
        !valuesCasted.italianDelegating?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italianDelegating.municipality`
        );
      }

      return true;
    })
    .test('foreignDelegating', 'Campo obbligatorio', (values) => {
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeignDelegating && !valuesCasted.foreignDelegating?.state?.id) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreignDelegating.state`
        );
      }

      return true;
    });
};

const schemaMADTSP6 = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      publication: yup.string().nullable().required('Campo obbligatorio'),
      ageDefect: yup.string().nullable().required('Campo obbligatorio'),
      relationship: yup.string().nullable().required('Campo obbligatorio'),
      place: yup.string().nullable().required('Campo obbligatorio'),
      presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
      propertyRegime: yup.string().nullable().notRequired(),
      minorWifeAssistance: yup
        .string()
        .nullable()
        .when(['ageDefect', 'propertyRegime'], {
          is: (ageDefect: string, propertyRegime: string) =>
            propertyRegime === '2' && (ageDefect === '3' || ageDefect === '4'),
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      minorGroomAssistance: yup
        .string()
        .nullable()
        .when(['ageDefect', 'propertyRegime'], {
          is: (ageDefect: string, propertyRegime: string) =>
            propertyRegime === '2' && (ageDefect === '2' || ageDefect === '4'),
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      declarant: yup.string().nullable().required('Campo obbligatorio'),
      ritualReligious: yup.object().shape({}).required('Campo obbligatorio'),
      otherRitualReligious: yup
        .string()
        .nullable()
        .when('ritualReligious', {
          is: (ritualReligious: ItemVM) =>
            ritualReligious?.id?.toString() === ID_OTHER_RITUAL_RELIGIOUS,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaMADTSP7 = () => {
  return yup.object().shape({
    dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
    ageDefect: yup.string().nullable().required('Campo obbligatorio'),
    relationship: yup.string().nullable().required('Campo obbligatorio'),
    place: yup.string().nullable().required('Campo obbligatorio'),
    presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
    propertyRegime: yup.string().nullable().notRequired(),
    minorWifeAssistance: yup
      .string()
      .nullable()
      .when(['ageDefect', 'propertyRegime'], {
        is: (ageDefect: string, propertyRegime: string) =>
          propertyRegime === '2' && (ageDefect === '3' || ageDefect === '4'),
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    minorGroomAssistance: yup
      .string()
      .nullable()
      .when(['ageDefect', 'propertyRegime'], {
        is: (ageDefect: string, propertyRegime: string) =>
          propertyRegime === '2' && (ageDefect === '2' || ageDefect === '4'),
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    address: yup.string().nullable().required('Campo obbligatorio'),
    houseNumber: yup.string().nullable().required('Campo obbligatorio'),
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
      }),
  });
};

const schemaUCDTSU01 = () => {
  return yup.object().shape({
    isMaintenanceAllowance: yup.boolean().nullable(),
    maintenanceAllowance: yup
      .string()
      .nullable()
      .when('isMaintenanceAllowance', {
        is: (isMaintenanceAllowance: boolean) => isMaintenanceAllowance,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    isPendingJudgment: yup.boolean().nullable(),
    judicialAuthority: yup
      .string()
      .nullable()
      .when('isPendingJudgment', {
        is: (isPendingJudgment: boolean) => isPendingJudgment,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    dateMarriage: yup.date().typeError('Data non valida').nullable(),
    dateAgreementConfirmation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    italian: yup.object().nullable().shape({}),
    foreign: yup.object().nullable().shape({}),
    celebratedBy: yup.string().nullable(),
  });
};

const schemaSEPAR01 = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isMaintenanceAllowance: yup.boolean().nullable(),
      maintenanceAllowance: yup
        .string()
        .nullable()
        .when('isMaintenanceAllowance', {
          is: (isMaintenanceAllowance: boolean) => isMaintenanceAllowance,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      isPendingJudgment: yup.boolean().nullable(),
      judicialAuthority: yup
        .string()
        .nullable()
        .when('isPendingJudgment', {
          is: (isPendingJudgment: boolean) => isPendingJudgment,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      dateMarriage: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      dateAgreementConfirmation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
      celebratedBy: yup.string().nullable().required('Campo obbligatorio'),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaSEPAR02 = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isMaintenanceAllowance: yup.boolean().nullable(),
      maintenanceAllowance: yup
        .string()
        .nullable()
        .when('isMaintenanceAllowance', {
          is: (isMaintenanceAllowance: boolean) => isMaintenanceAllowance,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      isPendingJudgment: yup.boolean().nullable(),
      judicialAuthority: yup
        .string()
        .nullable()
        .when('isPendingJudgment', {
          is: (isPendingJudgment: boolean) => isPendingJudgment,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      dateMarriage: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      dateAgreementConfirmation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
      celebratedBy: yup.string().nullable().required('Campo obbligatorio'),
      typeAgreement: yup.string().nullable().required('Campo obbligatorio'),
      typeSeparation: yup.string().nullable().required('Campo obbligatorio'),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaSEPAR04 = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isMaintenanceAllowance: yup.boolean().nullable(),
      maintenanceAllowance: yup
        .string()
        .nullable()
        .when('isMaintenanceAllowance', {
          is: (isMaintenanceAllowance: boolean) => isMaintenanceAllowance,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      isPendingJudgment: yup.boolean().nullable(),
      judicialAuthority: yup
        .string()
        .nullable()
        .when('isPendingJudgment', {
          is: (isPendingJudgment: boolean) => isPendingJudgment,
          then: yup.string().nullable().required('Campo obbligatorio'),
          otherwise: yup.string().nullable(),
        }),
      dateMarriage: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      dateAgreementConfirmation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
      celebratedBy: yup.string().nullable().required('Campo obbligatorio'),
      typeEvent: yup.string().nullable().required('Campo obbligatorio'),
      typeSeparation: yup.string().nullable().required('Campo obbligatorio'),
      templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaRICONC = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateMarriage: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
      celebratedBy: yup.string().nullable().required('Campo obbligatorio'),
      typeSeparation: yup.string().nullable().required('Campo obbligatorio'),
      dateReconciliation: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaBASEMTST = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaSEPAR03 = () => {
  return yup.object().shape({
    typeAgreement: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaUCDTSP02 = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
      numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
      genericImpediment: yup.string().nullable().required('Campo obbligatorio'),
      dataDelegate: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      ufcDelegate: yup.string().nullable().required('Campo obbligatorio'),
      reason: yup.string().nullable().required('Campo obbligatorio'),
      italianDelegating: yup.object().nullable().shape({}),
      foreignDelegating: yup.object().nullable().shape({}),
    })
    .test('italianDelegating', 'Campo obbligatorio', (values) => {
      const valuesCasted = values as unknown as SectionVM;

      if (
        !valuesCasted.isForeignDelegating &&
        !valuesCasted.italianDelegating?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italianDelegating.municipality`
        );
      }

      return true;
    })
    .test('foreignDelegating', 'Campo obbligatorio', (values) => {
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeignDelegating && !valuesCasted.foreignDelegating?.state?.id) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreignDelegating.state`
        );
      }

      return true;
    });
};

const schemaBASEUCST = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dataCivilUnion: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      isForeign: yup.boolean().nullable().notRequired(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaTRATXTUCSI = (keySubject: string) => {
  return yup
    .object()
    .shape({
      isForeign: yup.boolean().nullable().notRequired(),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    });
};

const schemaUCDTSP04 = (keySubject: string) => {
  return yup
    .object()
    .shape({
      dateEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      timeEvent: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
      presentationDocument: yup.string().nullable().required('Campo obbligatorio'),
      numberWitnesses: yup.string().nullable().required('Campo obbligatorio'),
      genericImpediment: yup.string().nullable().required('Campo obbligatorio'),
      italian: yup.object().nullable().shape({}),
      foreign: yup.object().nullable().shape({}),
      italianDelegating: yup.object().nullable().shape({}),
      foreignDelegating: yup.object().nullable().shape({}),
      dataDelegate: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Campo obbligatorio'),
      impedimentUnitedCivilly: yup.string().nullable().required('Campo obbligatorio'),
      houseNumber: yup.string().nullable().required('Campo obbligatorio'),
      address: yup.string().nullable().required('Campo obbligatorio'),
      place: yup.string().nullable().required('Campo obbligatorio'),
      ufcDelegate: yup.string().nullable().required('Campo obbligatorio'),
      reason: yup.string().nullable().required('Campo obbligatorio'),
    })
    .test('italian', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (!valuesCasted.isForeign && !valuesCasted.italian?.municipality?.description) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italian.municipality`
        );
      }

      return true;
    })
    .test('foreign', 'Campo obbligatorio', (values) => {
      // controllo italian
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeign && !valuesCasted.foreign?.state?.value) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreign.state`
        );
      }

      return true;
    })
    .test('italianDelegating', 'Campo obbligatorio', (values) => {
      const valuesCasted = values as unknown as SectionVM;

      if (
        !valuesCasted.isForeignDelegating &&
        !valuesCasted.italianDelegating?.municipality?.description
      ) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.italianDelegating.municipality`
        );
      }

      return true;
    })
    .test('foreignDelegating', 'Campo obbligatorio', (values) => {
      const valuesCasted = values as unknown as SectionVM;

      if (valuesCasted.isForeignDelegating && !valuesCasted.foreignDelegating?.state?.id) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.foreignDelegating.state`
        );
      }

      return true;
    });
};

const schemaCERTIF01 = () => {
  return yup.object().shape({
    dateIncorporationCertificate: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
      }),
  });
};

const schemaNEGOZ1 = () => {
  return yup.object().shape({
    typeAgreement: yup.string().nullable().required('Campo obbligatorio'),
    typeMeasure: yup.string().nullable().required('Campo obbligatorio'),
    receiptDocument: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaNEGOZ2 = () => {
  return yup.object().shape({
    documentDateFormation: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    italian: yup
      .object()
      .nullable()
      .shape({
        municipality: yup.object().nullable().shape({}).nullable().required('Campo obbligatorio'),
      }),
    depositDate: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    dateCommunication: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
  });
};

const schemaREGPAT01 = () => {
  return yup.object().shape({
    isWithNotarialDeed: yup.boolean().nullable(),
    propertyRegime: yup.string().nullable().required('Campo obbligatorio'),
    lawPropertyRegime: yup
      .string()
      .nullable()
      .when('propertyRegime', {
        is: (propertyRegime: string) => propertyRegime === IdPropertyRegime.AltraLeggeApplicabile,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    nameNotary: yup
      .string()
      .nullable()
      .when('isWithNotarialDeed', {
        is: (isWithNotarialDeed: boolean) => isWithNotarialDeed,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    surnameNotary: yup
      .string()
      .nullable()
      .when('isWithNotarialDeed', {
        is: (isWithNotarialDeed: boolean) => isWithNotarialDeed,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    notarialDistrict: yup
      .string()
      .nullable()
      .when('isWithNotarialDeed', {
        is: (isWithNotarialDeed: boolean) => isWithNotarialDeed,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    datePropertyRegime: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .when('isWithNotarialDeed', {
        is: (isWithNotarialDeed: boolean) => isWithNotarialDeed,
        then: yup.date().typeError('Data non valida').required('Campo obbligatorio'),
        otherwise: yup.date().nullable().typeError('Data non valida'),
      }),
    // repertoireNumber: yup
    //   .string()
    //   .nullable()
    //   .when('isWithNotarialDeed', {
    //     is: (isWithNotarialDeed: boolean) => isWithNotarialDeed,
    //     then: yup.string().nullable().required('Campo obbligatorio'),
    //     otherwise: yup.string().nullable(),
    //   }),
    // collectionNumber: yup
    //   .string()
    //   .nullable()
    //   .when('isWithNotarialDeed', {
    //     is: (isWithNotarialDeed: boolean) => isWithNotarialDeed,
    //     then: yup.string().nullable().required('Campo obbligatorio'),
    //     otherwise: yup.string().nullable(),
    //   }),
  });
};

const schemaCOMSCIO = () => {
  return yup.object().shape({
    dataSend: yup.date().typeError('Data non valida').nullable().required('Campo obbligatorio'),
    typeCommunication: yup.object().shape({}).required('Campo obbligatorio'),
    otherTypeCommunicationDescription: yup
      .string()
      .nullable()
      .when('typeCommunication', {
        is: (typeCommunication: ItemVM) =>
          typeCommunication?.id?.toString() === ID_COMMUNICATION.ID_ALTRO_MEZZO,
        then: yup.string().nullable().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
    address: yup.string().nullable().required('Campo obbligatorio'),
    dataReception: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
  });
};

const schemaGTRSCIT = () => {
  return yup.object().shape({
    parentTransmittedCitizenship: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaGTRSCIT2 = () => {
  return yup.object().shape({
    parentTransmittedCitizenship: yup.string().nullable().required('Campo obbligatorio'),
    dateTranscription: yup
      .date()
      .typeError('Data non valida')
      .nullable()
      .required('Campo obbligatorio'),
    templateTranscriptionText: yup.string().nullable().required('Campo obbligatorio'),
  });
};

const schemaANNCONT = (keySubject: string) => {
  return yup
    .object()
    .shape({
      annotations: yup.array().nullable(),
    })
    .test('conditionsItem', 'Campo obbligatorio', (values) => {
      // controllo Condizioni dichiarante se isDichiarante è true
      const valuesCasted = values as unknown as SectionVM;

      if ((valuesCasted.annotations || []).length === 0 || !valuesCasted.isSaved) {
        return new yup.ValidationError(
          'Campo obbligatorio',
          undefined,
          `${keySubject}.annotations`
        );
      }

      return true;
    });
};

const schemaNotRequired = (keySubject: string, isDichiarante: boolean, isFirmatario: boolean) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .nullable()
        .shape({
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          isMinorDeclarant: yup.boolean().nullable(),
          birthDate: yup.date().typeError('Data non valida').nullable(),
          conditionsItem: yup.object().shape({}),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItem?.id && isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (!valuesCasted.conditionsItemSignatory?.id && isFirmatario && !isDichiarante) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        }),
    });
};

const schemaNotRequiredC = (
  keySubject: string,
  isDichiarante: boolean,
  isFirmatario: boolean,
  dateFormationAct: DateTime
) => {
  return yup
    .object()
    .nullable()
    .shape({
      generality: yup
        .object()
        .nullable()
        .shape({
          fiscalCode: yup
            .string()
            .notRequired()
            .matches(fiscalCodeRegex, 'Il campo deve essere un codice fiscale'),
          isMinorDeclarant: yup.boolean().nullable(),
          birthDate: yup
            .date()
            .typeError('Data non valida')
            .nullable()
            .when('isMinorDeclarant', {
              is: (isMinorDeclarant: boolean) => isMinorDeclarant,
              then: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  dateFormationAct.plus({ day: 1 }),
                  "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
              otherwise: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .max(
                  isDichiarante
                    ? dateFormationAct.minus({ years: 18 })
                    : dateFormationAct.plus({ day: 1 }),
                  isDichiarante
                    ? 'Il soggetto deve essere maggiorenne'
                    : "La data deve essere minore uguale della data di formazione dell'atto"
                )
                .required('Campo obbligatorio'),
            }),
          conditionsItem: yup.object().shape({}),
        })
        .test('gender', 'Campo obbliatorio condizionato', (values) => {
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            arrSubjectControllGenderF.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.MASCHIO &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere una femmina',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          if (
            arrSubjectControllGenderM.includes(keySubject) &&
            Boolean(valuesCasted.gender) &&
            valuesCasted.gender === ID_GENDERS.FEMMINA &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Il soggetto deve essere un maschio',
              undefined,
              `${keySubject}.generality.gender`
            );
          }

          return true;
        })
        .test('conditionsItem', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItem?.id &&
            isDichiarante &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItem`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItemSignatory?.id &&
            isFirmatario &&
            !isDichiarante &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        })
        .test('conditionsItemSignatory', 'Campo obbligatorio', (values) => {
          // controllo Condizioni dichiarante se isDichiarante è true
          const valuesCasted = values as unknown as AccordionGeneralityForm;

          if (
            !valuesCasted.conditionsItemSignatory?.id &&
            isFirmatario &&
            !isDichiarante &&
            valuesCasted.isRequiredFromOtherAccordion
          ) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `${keySubject}.generality.conditionsItemSignatory`
            );
          }

          return true;
        }),
    });
};

export interface InputCreate {
  configuration: string;
  keySubject: string;
  codeSubject?: string;
  presenza?: string;
  isDichiarante?: boolean;
  isFirmatario?: boolean;
}

export const createSchemaSubjectSectionStep = (
  data: Array<InputCreate>,
  dateFormationAct: DateTime,
  withReasonDelay: boolean
): yup.ObjectSchema<ObjectShape, Record<string, unknown>> => {
  const validation: Record<string, unknown> = {};

  data.forEach((item) => {
    const isDichiarante: boolean = item.isDichiarante || false;
    const isFirmatario: boolean = item.isFirmatario || false;
    if (item.presenza === typePresence.Default1) {
      validation[item.keySubject] = schemaNotRequired(item.keySubject, isDichiarante, isFirmatario);

      return;
    }

    // condizionati: (condizionati da campi di altri accordion, se non vengono valorizzati non sono mostrati)
    if (item.presenza === typePresence.Condizionato) {
      switch (item.configuration) {
        case ConfigurationType.BASE:
        case ConfigurationType.BASESD:
        case ConfigurationType.BASEOPFI:
        case ConfigurationType.BASENOINT:
        case ConfigurationType.NEOSNOINT:
        case ConfigurationType.BASENOINTR:
        case ConfigurationType.BASENOINTP:
        case ConfigurationType.NEOSNOINTP:
        case ConfigurationType.NEOSPOS:
          validation[item.keySubject] = schemaBASEC(
            item.keySubject,
            isDichiarante,
            isFirmatario,
            dateFormationAct
          );
          break;
        case ConfigurationType.FIGLIOTRNA:
          validation[item.keySubject] = schemaFIGLIOTRNAC(
            item.keySubject,
            isDichiarante,
            isFirmatario,
            dateFormationAct
          );
          break;
        case ConfigurationType.BASE02:
          validation[item.keySubject] = schemaBASE02C(
            item.keySubject,
            isDichiarante,
            isFirmatario,
            dateFormationAct
          );
          break;
        case ConfigurationType.BASE02OPFI:
          validation[item.keySubject] = schemaBASE02OPFI(
            item.keySubject,
            isDichiarante,
            isFirmatario,
            dateFormationAct
          );
          break;
        case ConfigurationType.ATCOBASE:
        case ConfigurationType.ATCOBASEN:
        case ConfigurationType.ATCOBASED:
        case ConfigurationType.ATCOBASEDN:
        case ConfigurationType.ATCOBASEC:
          validation[item.keySubject] = schemaATCOBASEC(item.keySubject);
          break;
        case ConfigurationType.ATCOBASECP:
          validation[item.keySubject] = schemaATCOBASECP(item.keySubject);
          break;
        case ConfigurationType.BASERICAU:
          validation[item.keySubject] = schemaBASERICAUC(item.keySubject);
          break;
        case ConfigurationType.GIURCIT2:
          validation[item.keySubject] = schemaGIURCIT2C();
          break;
        case ConfigurationType.ENTEDICHC:
          validation[item.keySubject] = schemaENTEDICHCF(item.keySubject);
          break;
        case ConfigurationType.ENTEDICHIF:
          validation[item.keySubject] = schemaENTEDICHIF();
          break;
        case ConfigurationType.ENTEDICHIT:
          validation[item.keySubject] = schemaENTEDICHITF(item.keySubject);
          break;
        case ConfigurationType.ASSFIG:
          validation[item.keySubject] = schemaASSFIG();
          break;
        case ConfigurationType.ASSRESO:
          validation[item.keySubject] = schemaASSRESO();
          break;
        case ConfigurationType.PUBBLI:
        case ConfigurationType.PUBBLIDUE:
          validation[item.keySubject] = schemaPUBBLI(item.keySubject);
          break;
        case ConfigurationType.ATCOFA:
        case ConfigurationType.ATCOFAN:
          validation[item.keySubject] = schemaATCOFAC(item.keySubject);
          break;
        case ConfigurationType.IMPGEN:
          validation[item.keySubject] = schemaIMPGENF();
          break;
        case ConfigurationType.IMPED01:
          validation[item.keySubject] = schemaIMPED01F(item.keySubject);
          break;
        case ConfigurationType.RIC1:
        case ConfigurationType.RIC2:
        case ConfigurationType.RIC3:
        case ConfigurationType.RIC4:
        case ConfigurationType.RIC5:
        case ConfigurationType.RIC6:
        case ConfigurationType.RIC7:
          validation[item.keySubject] = schemaRICC(
            item.keySubject,
            isDichiarante,
            isFirmatario,
            dateFormationAct
          );
          break;
        case ConfigurationType.BASEAVV:
        case ConfigurationType.BASEAVV4:
          validation[item.keySubject] = schemaBASEAVVC();
          break;
        case ConfigurationType.BASEAVV2:
          validation[item.keySubject] = schemaBASEAVV2C();
          break;
        case ConfigurationType.PROVCOM:
          validation[item.keySubject] = schemaPROVCOMC();
          break;
        case ConfigurationType.TRATXBASR:
          validation[item.keySubject] = schemaTRATXBASRC();
          break;
        case ConfigurationType.BASECONI:
        case ConfigurationType.CONIOPFI:
          validation[item.keySubject] = schemaBASECONIC();
          break;
        case ConfigurationType.SOGMIN:
        case ConfigurationType.SOGMINA:
          validation[item.keySubject] = schemaSOGMINC();
          break;
        case ConfigurationType.BASETRASC:
        case ConfigurationType.BASEFOPFI:
          validation[item.keySubject] = schemaNotRequiredC(
            item.keySubject,
            isDichiarante,
            isFirmatario,
            dateFormationAct
          );
          break;
        case ConfigurationType.NASC03:
          validation[item.keySubject] = schemaNASC03C(
            item.keySubject,
            isDichiarante,
            isFirmatario,
            dateFormationAct,
            withReasonDelay
          );
          break;
        case ConfigurationType.DGRECUP:
        case ConfigurationType.DGRECUP1:
          validation[item.keySubject] = schemaDGRECUPC();
          break;
        case ConfigurationType.DGRECUP2:
          validation[item.keySubject] = schemaDGRECUP2C();
          break;
        case ConfigurationType.TRASDATIAF:
          validation[item.keySubject] = schemaTRASDATIAC(item.keySubject);
          break;
        case ConfigurationType.TRASDATIAV:
          validation[item.keySubject] = schemaTRASDATIAVC(item.keySubject);
          break;
        default:
          break;
      }

      return;
    }

    // facoltativi: (sempre mostrati e facoltativi ma obbligatori se viene inserito un qualsiasi campo)
    if (item.presenza === typePresence.Facoltativo) {
      switch (item.configuration) {
        case ConfigurationType.ATCOBASE:
        case ConfigurationType.ATCOBASEN:
        case ConfigurationType.ATCOBASED:
        case ConfigurationType.ATCOBASEDN:
        case ConfigurationType.ATCOBASEC:
          validation[item.keySubject] = schemaAtcoBaseF(item.keySubject);
          break;
        case ConfigurationType.BASE:
        case ConfigurationType.NEOSPOS:
        case ConfigurationType.BASEOPFI:
        case ConfigurationType.BASESD:
          validation[item.keySubject] = schemaBaseF(
            item.keySubject,
            isDichiarante,
            isFirmatario,
            dateFormationAct
          );
          break;
        case ConfigurationType.SOGMIN:
        case ConfigurationType.SOGMINA:
          validation[item.keySubject] = schemaSOGMINF(item.keySubject);
          break;
        case ConfigurationType.OFFI_CIV:
        case ConfigurationType.OFFI_REL:
        case ConfigurationType.OFFI_CITT:
        case ConfigurationType.OFFI_TRAS:
          validation[item.keySubject] = schemaOFFICIVF(item.keySubject);
          break;
        case ConfigurationType.ULT_LUOGO:
          validation[item.keySubject] = schemaULTLUOGOF();
          break;
        case ConfigurationType.CERTIF01:
          validation[item.keySubject] = schemaCERTIF01F(item.keySubject);
          break;
        case ConfigurationType.INTEGRAZ01:
          validation[item.keySubject] = schemaINTEGRAZ01F();
          break;
        default:
          break;
      }

      return;
    }

    // facoltativi: (sempre mostrati e facoltativi ma obbligatori se viene inserito un qualsiasi campo)
    if (item.presenza === typePresence.Facoltativo) {
      switch (item.configuration) {
        case ConfigurationType.ATCOBASE:
        case ConfigurationType.ATCOBASEN:
        case ConfigurationType.ATCOBASED:
        case ConfigurationType.ATCOBASEDN:
        case ConfigurationType.ATCOBASEC:
          validation[item.keySubject] = schemaAtcoBaseF(item.keySubject);
          break;
        case ConfigurationType.BASE:
        case ConfigurationType.NEOSPOS:
        case ConfigurationType.BASEOPFI:
        case ConfigurationType.BASESD:
          validation[item.keySubject] = schemaBaseF(
            item.keySubject,
            isDichiarante,
            isFirmatario,
            dateFormationAct
          );
          break;
        case ConfigurationType.SOGMIN:
        case ConfigurationType.SOGMINA:
          validation[item.keySubject] = schemaSOGMINF(item.keySubject);
          break;
        case ConfigurationType.OFFI_CIV:
        case ConfigurationType.OFFI_REL:
        case ConfigurationType.OFFI_CITT:
        case ConfigurationType.OFFI_TRAS:
          validation[item.keySubject] = schemaOFFICIVF(item.keySubject);
          break;
        case ConfigurationType.OFFI_TRASA:
          validation[item.keySubject] = schemaOFFITRASAF(item.keySubject);
          break;
        case ConfigurationType.ULT_LUOGO:
          validation[item.keySubject] = schemaULTLUOGOF();
          break;
        case ConfigurationType.CERTIF01:
          validation[item.keySubject] = schemaCERTIF01F(item.keySubject);
          break;
        case ConfigurationType.INTEGRAZ01:
          validation[item.keySubject] = schemaINTEGRAZ01F();
          break;
        default:
          break;
      }

      return;
    }

    // FacoltativoObbligatorio: (Come Facoltativo e Condizionato insieme)
    if (item.presenza === typePresence.FacoltativoObbligatorio) {
      switch (item.configuration) {
        case ConfigurationType.RIC1:
        case ConfigurationType.RIC2:
          validation[item.keySubject] = schemaRIC1FO(
            item.keySubject,
            isDichiarante,
            dateFormationAct
          );
          break;
        case ConfigurationType.RIC3:
        case ConfigurationType.RIC4:
        case ConfigurationType.RIC5:
        case ConfigurationType.RIC6:
        case ConfigurationType.RIC7:
          validation[item.keySubject] = schemaRICFO(
            item.keySubject,
            isDichiarante,
            dateFormationAct
          );
          break;
        default:
          break;
      }

      return;
    }

    // obbligatori: (sempre richiesti)
    switch (item.configuration) {
      case ConfigurationType.BASETRASC:
      case ConfigurationType.BASEFOPFI:
        validation[item.keySubject] = schemaNotRequired(
          item.keySubject,
          isDichiarante,
          isFirmatario
        );
        break;
      case ConfigurationType.BASE:
      case ConfigurationType.BASESD:
      case ConfigurationType.BASEOPFI:
      case ConfigurationType.BASENOINT:
      case ConfigurationType.BASENOINTR:
      case ConfigurationType.BASENOINTP:
      case ConfigurationType.INTCIT2:
      case ConfigurationType.INTCIT4:
        validation[item.keySubject] = schemaBASE(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASEANMD:
        validation[item.keySubject] = schemaBASEANMD(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASEANNI:
        validation[item.keySubject] = schemaBASEANNI(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASEMAG:
        validation[item.keySubject] = schemaBASEMAG(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.NEOSNOINT:
      case ConfigurationType.NEOSNOINTP:
      case ConfigurationType.NEOSPOS:
        validation[item.keySubject] = schemaNEO(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASERESMI:
        validation[item.keySubject] = schemaBASEREMI(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.RICH_UCOPF:
        validation[item.keySubject] = schemaRICHUCOPF(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASE14:
        validation[item.keySubject] = schemaBASE14(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASEMIN:
        validation[item.keySubject] = schemaBASEMIN(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASE02:
      case ConfigurationType.BASE02OPFI:
        validation[item.keySubject] = schemaBASE02(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASE02A:
        validation[item.keySubject] = schemaBASE02A(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASEDIR:
        validation[item.keySubject] = schemaBASEDIR(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASEG:
        validation[item.keySubject] = schemaBASEG(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.PERIC_CIV:
        validation[item.keySubject] = schemaPERICCIV(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.INTCIT:
        validation[item.keySubject] = schemaINTCIT(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.INTCIT3:
        validation[item.keySubject] = schemaINTCIT3(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASEDEC:
      case ConfigurationType.BASESCATTO:
        validation[item.keySubject] = schemaBASEDEC(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASE02DEC:
        validation[item.keySubject] = schemaBASE02DEC(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.TRATXTBASE:
      case ConfigurationType.TRATXTSCR:
        validation[item.keySubject] = schemaTRATXTBASE();
        break;
      case ConfigurationType.TRANEGASS:
        validation[item.keySubject] = schemaTRANEGASS();
        break;
      case ConfigurationType.TRATXTBSDE:
        validation[item.keySubject] = schemaTRATXTBSDE();
        break;
      case ConfigurationType.TRATXTBSDF:
        validation[item.keySubject] = schemaTRATXTBSDF();
        break;
      case ConfigurationType.TRATXTUCE:
        validation[item.keySubject] = schemaTRATXTUCE();
        break;
      case ConfigurationType.TRATXTUC2:
        validation[item.keySubject] = schemaTRATXTUC2();
        break;
      case ConfigurationType.TRATXTUC3:
        validation[item.keySubject] = schemaTRATXTUC3();
        break;
      case ConfigurationType.TRATXTMTE:
        validation[item.keySubject] = schemaTRATXTMTE(item.keySubject);
        break;
      case ConfigurationType.TRATXTMTAE:
        validation[item.keySubject] = schemaTRATXTMTAE(item.keySubject);
        break;
      case ConfigurationType.TRATXTBASI:
        validation[item.keySubject] = schemaTRATXTBASI();
        break;
      case ConfigurationType.TRATXTBASG:
        validation[item.keySubject] = schemaTRATXTBASG();
        break;
      case ConfigurationType.TRATXTBASN:
        validation[item.keySubject] = schemaTRATXTBASN();
        break;
      case ConfigurationType.TRATXBASRP:
        validation[item.keySubject] = schemaTRATXBASRP();
        break;
      case ConfigurationType.TRATXTBASV:
        validation[item.keySubject] = schemaTRATXTBASV();
        break;
      case ConfigurationType.TRASCRTISC:
      case ConfigurationType.TRATXTTISC:
        validation[item.keySubject] = schemaTRASCRTISC();
        break;
      case ConfigurationType.TRATXBASF:
        validation[item.keySubject] = schemaTRATXBASF();
        break;
      case ConfigurationType.TRATXBASS:
        validation[item.keySubject] = schemaTRATXBASS();
        break;
      case ConfigurationType.TRATXBASC:
        validation[item.keySubject] = schemaTRATXBASC();
        break;
      case ConfigurationType.TRATXBASD:
        validation[item.keySubject] = schemaTRATXBASD();
        break;
      case ConfigurationType.TRATXBASQ:
        validation[item.keySubject] = schemaTRATXBASQ();
        break;
      case ConfigurationType.TRATXTBASC:
        validation[item.keySubject] = schemaTRATXTBASC();
        break;
      case ConfigurationType.TRASTXTRIC:
        validation[item.keySubject] = schemaTRASTXTRIC();
        break;
      case ConfigurationType.TRATXBASR:
        validation[item.keySubject] = schemaTRATXBASR();
        break;
      case ConfigurationType.TRATXBASRN:
      case ConfigurationType.TRATXBASRQ:
        validation[item.keySubject] = schemaTRATXBASRN();
        break;
      case ConfigurationType.DTGANNMO:
        validation[item.keySubject] = schemaDTGANNMO();
        break;
      case ConfigurationType.TRATXBASRX:
        validation[item.keySubject] = schemaTRATXBASRX();
        break;
      case ConfigurationType.TRATXBASRO:
        validation[item.keySubject] = schemaTRATXTBASRO();
        break;
      case ConfigurationType.TRATXTBASF:
        validation[item.keySubject] = schemaTRATXTBASF();
        break;
      case ConfigurationType.TRASNASLU:
        validation[item.keySubject] = schemaTRASNASLU();
        break;
      case ConfigurationType.BASECONI:
      case ConfigurationType.CONIOPFI:
        validation[item.keySubject] = schemaBASECONI();
        break;
      case ConfigurationType.SOGMIN:
      case ConfigurationType.SOGMINA:
        validation[item.keySubject] = schemaSOGMIN();
        break;
      case ConfigurationType.OFFI_CIV:
      case ConfigurationType.OFFI_REL:
      case ConfigurationType.OFFI_CITT:
      case ConfigurationType.OFFI_TRAS:
        validation[item.keySubject] = schemaOFFICIV();
        break;
      case ConfigurationType.OFFI_ACATT:
        validation[item.keySubject] = schemaOFFIACATT();
        break;
      case ConfigurationType.SEGR_CIV:
        validation[item.keySubject] = schemaSEGRCIV();
        break;
      case ConfigurationType.BASEM:
      case ConfigurationType.BASEP:
      case ConfigurationType.SENPROCREP:
        validation[item.keySubject] = schemaBASEM();
        break;
      case ConfigurationType.BASERIC:
        validation[item.keySubject] = schemaBASERIC();
        break;
      case ConfigurationType.BASERICNSE:
        validation[item.keySubject] = schemaBASERICNSE();
        break;
      case ConfigurationType.BASERICAU:
        validation[item.keySubject] = schemaBASERICAU();
        break;
      case ConfigurationType.RICSECONS:
        validation[item.keySubject] = schemaRICSECONS(item.keySubject);
        break;
      case ConfigurationType.BASEDECA:
        validation[item.keySubject] = schemaBASEDECA(item.keySubject);
        break;
      case ConfigurationType.BASEDECO:
      case ConfigurationType.BASEDECOV:
        validation[item.keySubject] = schemaBASEDECO(item.keySubject);
        break;
      case ConfigurationType.BASEDECNID:
        validation[item.keySubject] = schemaBASEDECNID(item.keySubject);
        break;
      case ConfigurationType.DATIDECEST:
        validation[item.keySubject] = schemaDATIDECEST(item.keySubject);
        break;
      case ConfigurationType.DATIDECGEN:
        validation[item.keySubject] = schemaDATIDECGEN(item.keySubject);
        break;
      case ConfigurationType.BASECIT06:
        validation[item.keySubject] = schemaBASECIT06();
        break;
      case ConfigurationType.BASECIT07:
        validation[item.keySubject] = schemaBASECIT07();
        break;
      case ConfigurationType.BASEUC:
        validation[item.keySubject] = schemaBASEUC(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASEPERD:
        validation[item.keySubject] = schemaBASEPERD(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.BASEAVV:
      case ConfigurationType.BASEAVV4:
        validation[item.keySubject] = schemaBASEAVV();
        break;
      case ConfigurationType.RIC1:
      case ConfigurationType.RIC2:
      case ConfigurationType.RIC3:
      case ConfigurationType.RIC4:
        validation[item.keySubject] = schemaRIC(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.NASC01:
        validation[item.keySubject] = schemaNASC(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct,
          withReasonDelay
        );
        break;
      case ConfigurationType.NASC04:
      case ConfigurationType.NASC02:
        validation[item.keySubject] = schemaNASC04(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct,
          withReasonDelay
        );
        break;
      case ConfigurationType.NASC05:
        validation[item.keySubject] = schemaNASC05(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct,
          withReasonDelay
        );
        break;
      case ConfigurationType.NASC06:
        validation[item.keySubject] = schemaNASC06(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct,
          withReasonDelay
        );
        break;
      case ConfigurationType.NASC99:
        validation[item.keySubject] = schemaNASC99(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct,
          withReasonDelay
        );
        break;
      case ConfigurationType.NASC03:
        validation[item.keySubject] = schemaNASC03(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct,
          withReasonDelay
        );
        break;
      case ConfigurationType.NASC07:
        validation[item.keySubject] = schemaNASC07(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct,
          withReasonDelay
        );
        break;
      case ConfigurationType.SANIT01:
        validation[item.keySubject] = schemaSANIT01(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.PROCUR01:
        validation[item.keySubject] = schemaPROCUR01(
          item.keySubject,
          isDichiarante,
          isFirmatario,
          dateFormationAct
        );
        break;
      case ConfigurationType.TRASDATIAT:
        validation[item.keySubject] = schemaTRASDATIAT();
        break;
      case ConfigurationType.TRAEDATIAT:
        validation[item.keySubject] = schemaTRAEDATIAT(item.keySubject);
        break;
      case ConfigurationType.TRASDATIAU:
        validation[item.keySubject] = schemaTRASDATIAU(item.keySubject);
        break;
      case ConfigurationType.TRASDATIAF:
        validation[item.keySubject] = schemaTRASDATIA(item.keySubject);
        break;
      case ConfigurationType.AUMITITA:
        validation[item.keySubject] = schemaAUMITITA();
        break;
      case ConfigurationType.TRASDATIAV:
        validation[item.keySubject] = schemaTRASDATIAV();
        break;
      case ConfigurationType.ENTEDICHAC:
        validation[item.keySubject] = schemaENTEDICHAC(item.keySubject);
        break;
      case ConfigurationType.TRAEDATIAU:
        validation[item.keySubject] = schemaTRAEDATIAU(item.keySubject);
        break;
      case ConfigurationType.MORDATIAU:
        validation[item.keySubject] = schemaMORDATIAU();
        break;
      case ConfigurationType.PROVCOM:
        validation[item.keySubject] = schemaPROVCOM();
        break;
      case ConfigurationType.RICATTCONS:
        validation[item.keySubject] = schemaRICATTCONS();
        break;
      case ConfigurationType.CSAD:
      case ConfigurationType.DPSM:
      case ConfigurationType.TRDATIATAU:
      case ConfigurationType.ENTEDICH:
      case ConfigurationType.ENTEDICHT:
        validation[item.keySubject] = schemaCSAD(item.keySubject);
        break;
      case ConfigurationType.ENTEDICHS:
        validation[item.keySubject] = ENTEDICHS(item.keySubject);
        break;
      case ConfigurationType.TRDATIATIN:
        validation[item.keySubject] = schemaTRDATIATIN(item.keySubject);
        break;
      case ConfigurationType.CSRA:
        validation[item.keySubject] = schemaCSRA();
        break;
      case ConfigurationType.DSCIT:
      case ConfigurationType.DSCIT3:
      case ConfigurationType.DSCITFR:
        validation[item.keySubject] = schemaDSCIT();
        break;
      case ConfigurationType.DSCIT2:
        validation[item.keySubject] = schemaDSCIT2();
        break;
      case ConfigurationType.DSCIT4:
        validation[item.keySubject] = schemaDSCIT4();
        break;
      case ConfigurationType.DSCIT6:
        validation[item.keySubject] = schemaDSCIT6();
        break;
      case ConfigurationType.DSCIT7:
        validation[item.keySubject] = schemaDSCIT7();
        break;
      case ConfigurationType.DSCIT8:
        validation[item.keySubject] = schemaDSCIT8();
        break;
      case ConfigurationType.DSCIT9:
        validation[item.keySubject] = schemaDSCIT9();
        break;
      case ConfigurationType.DSCIT10:
        validation[item.keySubject] = schemaDSCIT10();
        break;
      case ConfigurationType.DPIMP:
        validation[item.keySubject] = schemaDPIMP(item.keySubject);
        break;
      case ConfigurationType.ATCOBASE:
      case ConfigurationType.ATCOBASEN:
      case ConfigurationType.ATCOBASED:
      case ConfigurationType.ATCOBASEDN:
      case ConfigurationType.ATCOBASEC:
      case ConfigurationType.ATCODRM:
      case ConfigurationType.ATCODRMA:
      case ConfigurationType.ATCODRMM:
      case ConfigurationType.ATCODRMU:
        const isAtcoDRM = item.configuration === ConfigurationType.ATCODRM;
        validation[item.keySubject] = schemaATCOBASE(item.keySubject, isAtcoDRM);
        break;
      case ConfigurationType.ATCOFA:
      case ConfigurationType.ATCOFAN:
        validation[item.keySubject] = schemaATCOFA(item.keySubject);
        break;
      case ConfigurationType.COMPLIB01:
      case ConfigurationType.COMPLIB02:
      case ConfigurationType.COMPLIB03:
        validation[item.keySubject] = schemaCOMPLIB01();
        break;
      case ConfigurationType.DGRECUP:
      case ConfigurationType.DGRECUP1:
        validation[item.keySubject] = schemaDGRECUP();
        break;
      case ConfigurationType.DGRECUP2:
        validation[item.keySubject] = schemaDGRECUP2();
        break;
      case ConfigurationType.RICDGCSAS:
        validation[item.keySubject] = schemaRICDGCSAS();
        break;
      case ConfigurationType.RICDGCSSE:
        validation[item.keySubject] = schemaRICDGCSSE();
        break;
      case ConfigurationType.RICDGAS:
        validation[item.keySubject] = schemaRICDGAS();
        break;
      case ConfigurationType.RICDGP:
      case ConfigurationType.UCDTSP06:
        validation[item.keySubject] = schemaRICDGP();
        break;
      case ConfigurationType.RICCONS:
        validation[item.keySubject] = schemaRICCONS();
        break;
      case ConfigurationType.RICASSE:
        validation[item.keySubject] = schemaRICASSE();
        break;
      case ConfigurationType.RICDGCS:
        validation[item.keySubject] = schemaRICDGCS();
        break;
      case ConfigurationType.RICDGCSASC:
        validation[item.keySubject] = schemaRICDGCSASC();
        break;
      case ConfigurationType.DICHNNOCON:
        validation[item.keySubject] = schemaDICHNNOCON();
        break;
      case ConfigurationType.RICGENIT:
        validation[item.keySubject] = schemaRICGENIT();
        break;
      case ConfigurationType.ESIACSI:
        validation[item.keySubject] = schemaESIACSI();
        break;
      case ConfigurationType.ESIACSI2:
        validation[item.keySubject] = schemaESIACSI2();
        break;
      case ConfigurationType.GIURCIT:
        validation[item.keySubject] = schemaGIURCIT();
        break;
      case ConfigurationType.DSNAS01:
        validation[item.keySubject] = schemaDSNAS01();
        break;
      case ConfigurationType.DSNAS02:
        validation[item.keySubject] = schemaDSNAS02();
        break;
      case ConfigurationType.DSNAS03:
        validation[item.keySubject] = schemaDSNAS03();
        break;
      case ConfigurationType.DSNAS04:
        validation[item.keySubject] = schemaDSNAS04();
        break;
      case ConfigurationType.DSNAS05:
        validation[item.keySubject] = schemaDSNAS05();
        break;
      case ConfigurationType.UCDTSP:
        validation[item.keySubject] = schemaUCDTSP();
        break;
      case ConfigurationType.UCDTSP02:
        validation[item.keySubject] = schemaUCDTSP02(item.keySubject);
        break;
      case ConfigurationType.UCDTSP03:
        validation[item.keySubject] = schemaUCDTSP03();
        break;
      case ConfigurationType.UCDTSP04:
        validation[item.keySubject] = schemaUCDTSP04(item.keySubject);
        break;
      case ConfigurationType.UCDTSP05:
        validation[item.keySubject] = schemaUCDTSP05(item.keySubject);
        break;
      case ConfigurationType.MADTSP:
        validation[item.keySubject] = schemaMADTSP();
        break;
      case ConfigurationType.MADTSP2:
        validation[item.keySubject] = schemaMADTSP2(item.keySubject);
        break;
      case ConfigurationType.MADTSP3:
        validation[item.keySubject] = schemaMADTSP3();
        break;
      case ConfigurationType.MADTSP4:
        validation[item.keySubject] = schemaMADTSP4(item.keySubject);
        break;
      case ConfigurationType.MADTSP5:
        validation[item.keySubject] = schemaMADTSP5();
        break;
      case ConfigurationType.MADTSP6:
        validation[item.keySubject] = schemaMADTSP6(item.keySubject);
        break;
      case ConfigurationType.MADTSP7:
        validation[item.keySubject] = schemaMADTSP7();
        break;
      case ConfigurationType.MADTSP8:
        validation[item.keySubject] = schemaMADTSP8();
        break;
      case ConfigurationType.MADTSP9:
        validation[item.keySubject] = schemaMADTSP9(item.keySubject);
        break;
      case ConfigurationType.CERTIF01:
        validation[item.keySubject] = schemaCERTIF01();
        break;
      case ConfigurationType.ENTEDICHIT:
        validation[item.keySubject] = schemaENTEDICHIT();
        break;
      case ConfigurationType.ENTEDICHIN:
        validation[item.keySubject] = schemaENTEDICHIN();
        break;
      case ConfigurationType.ENTEDICHA:
        validation[item.keySubject] = schemaENTEDICHA();
        break;
      case ConfigurationType.ENTEDICHNC:
        validation[item.keySubject] = schemaENTEDICHNC(item.keySubject);
        break;
      case ConfigurationType.IMPGEN:
        validation[item.keySubject] = schemaIMPGEN();
        break;
      case ConfigurationType.IMPED01:
        validation[item.keySubject] = schemaIMPED01();
        break;
      case ConfigurationType.REGPAT01:
      case ConfigurationType.REGPAT02:
        validation[item.keySubject] = schemaREGPAT01();
        break;
      case ConfigurationType.COMSCIO:
        validation[item.keySubject] = schemaCOMSCIO();
        break;
      case ConfigurationType.GTRSCIT:
        validation[item.keySubject] = schemaGTRSCIT();
        break;
      case ConfigurationType.GTRSCIT2:
        validation[item.keySubject] = schemaGTRSCIT2();
        break;
      case ConfigurationType.ANNCONT:
      case ConfigurationType.ANNCONT105:
      case ConfigurationType.ANNCONT312:
        validation[item.keySubject] = schemaANNCONT(item.keySubject);
        break;
      case ConfigurationType.UCDTSU01:
        validation[item.keySubject] = schemaUCDTSU01();
        break;
      case ConfigurationType.SEPAR01:
        validation[item.keySubject] = schemaSEPAR01(item.keySubject);
        break;
      case ConfigurationType.SEPAR02:
        validation[item.keySubject] = schemaSEPAR02(item.keySubject);
        break;
      case ConfigurationType.SEPAR04:
        validation[item.keySubject] = schemaSEPAR04(item.keySubject);
        break;
      case ConfigurationType.SEPAR03:
      case ConfigurationType.SEPAR08:
        validation[item.keySubject] = schemaSEPAR03();
        break;
      case ConfigurationType.BASEUCST:
        validation[item.keySubject] = schemaBASEUCST(item.keySubject);
        break;
      case ConfigurationType.TRATXTUCSI:
        validation[item.keySubject] = schemaTRATXTUCSI(item.keySubject);
        break;
      case ConfigurationType.NEGOZ1:
        validation[item.keySubject] = schemaNEGOZ1();
        break;
      case ConfigurationType.NEGOZ2:
        validation[item.keySubject] = schemaNEGOZ2();
        break;
      case ConfigurationType.RICONC:
        validation[item.keySubject] = schemaRICONC(item.keySubject);
        break;
      case ConfigurationType.BASEAVV3:
        validation[item.keySubject] = schemaBASEAVV3();
        break;
      case ConfigurationType.BASEMTST:
        validation[item.keySubject] = schemaBASEMTST(item.keySubject);
        break;
      case ConfigurationType.ENTEDICHC:
        validation[item.keySubject] = schemaENTEDICHC(item.keySubject);
        break;
      case ConfigurationType.ENTEDICHE:
        validation[item.keySubject] = schemaENTEDICHE();
        break;
      case ConfigurationType.CITLEG:
        validation[item.keySubject] = schemaCITLEG();
        break;
      default:
        break;
    }
  });

  const validationRes: yup.ObjectSchema<ObjectShape, Record<string, unknown>> = yup
    .object()
    .nullable()
    .shape({ ...(validation as ObjectShape) }) as yup.ObjectSchema<
    ObjectShape,
    Record<string, unknown>
  >;

  return validationRes;
};
